import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, Drawer, Empty, Input, Select, Tooltip as AntTooltip } from "antd";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    Activity,
    AlertTriangle,
    Bot,
    ChevronLeft,
    ChevronRight,
    ClipboardList,
    Copy,
    Download,
    HelpCircle,
    Moon,
    RefreshCw,
    Search,
    Shield,
    Sun,
    Upload,
    Users,
} from "lucide-react";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";

import "./ArchimedeControlCenter.css";
import { formatNumber, formatUsd } from "../../lib/archimede-control-center/costCalculator";
import { parseObservabilityLogs } from "../../lib/archimede-control-center/logParser";
import { streamOpenAiExplanation } from "../../lib/archimede-control-center/openaiClient";
import { useArchimedeFilters } from "../../lib/archimede-control-center/useFilters";
import { fetchObservabilityRequestLogs, fetchObservabilitySecurityEvents, fetchObservabilityStories } from "../../Services/observability.service";
import { api } from "../../config";

const RANGE_OPTIONS = [
    { label: "Aujourd'hui", value: "today" },
    { label: "7 jours", value: "7d" },
    { label: "30 jours", value: "30d" },
    { label: "Personnalisé", value: "custom" },
];

const VIEW_OPTIONS = [
    { key: "control-room", label: "Control Room", icon: <AlertTriangle size={14} /> },
    { key: "overview", label: "Pulse", icon: <Activity size={14} /> },
    { key: "usage", label: "Clients & Users", icon: <Users size={14} /> },
    { key: "security", label: "Sécurité", icon: <Shield size={14} /> },
    { key: "inspector", label: "Inspecteur", icon: <ClipboardList size={14} /> },
];

const SERVICE_COLORS = {
    archimede: "#0B3D91",
    "verdictia-backend": "#1E5BB8",
};

const FEATURE_COLORS = {
    contract_profile_inventory: "#0B3D91",
    contract_profile_selection: "#1E5BB8",
    summary: "#1E5BB8",
    comment_details: "#8C8C89",
    prompt_generation: "#EF9F27",
    metadata_guess: "#2B9B95",
    other: "#8C8C89",
};

const FEATURE_LATENCY_ORDER = ["contract_profile_inventory", "contract_profile_selection", "metadata_guess", "summary", "comment_details", "prompt_generation"];

const STEP_COLORS = {
    input_preparation: "#8C8C89",
    prompt_construction: "#1E5BB8",
    streaming: "#0B3D91",
    contract_profile_streaming: "#0B3D91",
    pre_stream_completed: "#2B9B95",
};

const STATUS_COLORS = {
    success: "#639922",
    error: "#E24B4A",
    warning: "#EF9F27",
    info: "#1E5BB8",
};

const SECURITY_EVENT_COLORS = {
    login_success: "#5FA837",
    login_failed: "#EF9F27",
    account_locked: "#E24B4A",
    suspicious_ip: "#E24B4A",
    password_reset_requested: "#1E5BB8",
};

const CONTROL_ROOM_RECENT_WINDOW_MS = 24 * 60 * 60 * 1000;
const PRODUCTION_CRITICAL_FEATURES = ["contract_profile_inventory", "contract_profile_selection", "review", "analysis_stream", "comment_enrichment", "comment_details"];

function isEventLikeLogType(logType) {
    const value = String(logType || "").toLowerCase();
    if (!value) return false;
    if (value === "event_log") return true;
    if (value.startsWith("thinking_")) return true;
    return value === "retry" || value === "critical_error";
}

function inferOperationType(story) {
    const route = String(story?.route || "").toLowerCase();
    const feature = String(story?.feature || "").toLowerCase();
    const brain = String(story?.brain || "").toLowerCase();

    if (route.includes("/analyses/") && route.includes("launch-stream")) return "Run analysis";
    if (route.includes("/metadata/guess") || feature.includes("metadata_guess")) return "Metadata guess";
    if (route.includes("/contract-profile") || feature.includes("contract_profile") || brain.includes("contract_mechanism")) return "ContractProfile";
    if (route.includes("/summary") || feature.includes("summary") || brain.includes("summary")) return "Summary";
    if (route.includes("/comment-details") || feature.includes("comment") || brain.includes("comment")) return "Comment details";
    if (feature) return feature;
    return "Execution";
}

function extractRouteHint(story) {
    return story?.route || story?.endpoint || story?.path || story?.brain || "N.A";
}

function clipRequestId(requestId) {
    if (!requestId) return "n/a";
    return String(requestId).slice(0, 8);
}

function normalizeFeature(feature) {
    const text = String(feature || "").toLowerCase();
    if (!text) return "other";
    if (text.includes("contract_profile_inventory")) return "contract_profile_inventory";
    if (text.includes("contract_profile_selection")) return "contract_profile_selection";
    if (text.includes("metadata_guess")) return "metadata_guess";
    if (text.includes("prompt_generation") || text.includes("prompt-generation")) return "prompt_generation";
    if (text.includes("comment_details") || text.includes("comment-details")) return "comment_details";
    if (text.includes("summary")) return "summary";
    return text || "other";
}

function getStoryTimestampMs(story) {
    return toUnixMs(story?.timestamp || story?.ended_at || story?.started_at);
}

function isRecentStory(story, windowMs = CONTROL_ROOM_RECENT_WINDOW_MS) {
    const timestamp = getStoryTimestampMs(story);
    return timestamp != null && Date.now() - timestamp <= windowMs;
}

function isTestStory(story) {
    const hay = [
        story?.brain,
        story?.feature,
        story?.__normalized_feature,
        story?.route,
        story?.endpoint,
        story?.path,
        story?.execution_id,
        story?.request_id,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return /\btest[_-\s]?brain\b|\binternal[_-\s]?test\b|\btest\b/.test(hay);
}

function isRealActorId(value) {
    const text = String(value || "").trim().toLowerCase();
    if (!text) return false;
    return !["unknown", "unknown_user", "unknown_client", "n/a", "none", "null", "test", "test_user", "test_client"].includes(text);
}

function isProductionCriticalFeature(feature) {
    const normalized = normalizeFeature(feature);
    return PRODUCTION_CRITICAL_FEATURES.some((item) => normalized.includes(item));
}

function getStoryTraceKey(story) {
    if (story?.request_id) return String(story.request_id);
    if (story?.execution_id) return `exec:${story.execution_id}`;
    return `no-id:${story?.service || "unknown"}:${story?.timestamp || story?.started_at || "unknown"}:${story?.feature || "other"}`;
}

function getStorySelectionKey(story) {
    const traceKey = getStoryTraceKey(story);
    return [
        traceKey,
        story?.service || "unknown",
        story?.execution_id || story?.feature || story?.started_at || story?.timestamp || "no_exec",
    ].join(":");
}

function storyMatchesSelection(story, selectedKey) {
    if (!story || !selectedKey) return false;
    const selectionKey = story.__selection_key || getStorySelectionKey(story);
    const traceKey = story.__trace_key || getStoryTraceKey(story);
    if (selectionKey === selectedKey || traceKey === selectedKey) return true;
    if (selectionKey.startsWith(`${selectedKey}:`) || selectedKey.startsWith(`${selectionKey}:`)) return true;
    if (traceKey && selectedKey.startsWith(`${traceKey}:`)) {
        const selectedParts = selectedKey.split(":");
        return selectedParts.includes(story.service || "unknown") || selectedParts.includes(story.execution_id || "");
    }
    return Boolean(story.execution_id && selectedKey.includes(String(story.execution_id)));
}

function getStoryTelemetry(story) {
    const metadata = story?.metadata || {};
    const ttftUpstreamMs = Number(metadata.ttft_upstream_ms ?? story?.ttft_upstream_ms ?? NaN);
    const ttftLegacyMs = Number(metadata.time_to_first_token_ms ?? story?.time_to_first_token_ms ?? NaN);
    const chunksRaw = metadata.chunks_emitted ?? story?.chunks_emitted;
    const chunksParsed = Number(chunksRaw ?? NaN);
    const chunksEmitted = Number.isFinite(chunksParsed) ? chunksParsed : null;
    const effectiveTtftMs = Number.isFinite(ttftUpstreamMs)
        ? ttftUpstreamMs
        : Number.isFinite(ttftLegacyMs)
          ? ttftLegacyMs
          : null;

    return {
        preStreamDurationMs: Number(metadata.pre_stream_duration_ms ?? story?.pre_stream_duration_ms ?? 0) || 0,
        totalStreamDurationMs: Number(metadata.total_stream_duration_ms ?? story?.total_stream_duration_ms ?? 0) || 0,
        chunksEmitted,
        timeToFirstTokenMs: effectiveTtftMs,
        ttftControlMs: Number(metadata.ttft_control_ms ?? story?.ttft_control_ms ?? NaN),
        ttftUpstreamMs: Number.isFinite(ttftUpstreamMs) ? ttftUpstreamMs : null,
        cardsEmitted: metadata.cards_emitted ?? story?.cards_emitted ?? null,
        retryCount: Number(metadata.retry_count ?? story?.retry_count ?? 0) || 0,
        llmCallCount: Number(metadata.llm_call_count ?? story?.llm_call_count ?? 0) || 0,
        contextsCount: metadata.contexts_count ?? story?.contexts_count ?? null,
        jurisdictionsCount: metadata.jurisdictions_count ?? story?.jurisdictions_count ?? null,
        completionTokens: metadata.completion_tokens ?? story?.completion_tokens ?? null,
        bedrockReasoningEnabled: Boolean(metadata.bedrock_reasoning_enabled ?? story?.bedrock_reasoning_enabled ?? false),
        bedrockReasoningPublicStreamEnabled: Boolean(
            metadata.bedrock_reasoning_public_stream_enabled ?? story?.bedrock_reasoning_public_stream_enabled ?? false
        ),
        bedrockReasoningBudgetTokens: Number(metadata.bedrock_reasoning_budget_tokens ?? story?.bedrock_reasoning_budget_tokens ?? 0) || 0,
        bedrockReasoningDeltaCount: Number(metadata.bedrock_reasoning_delta_count ?? story?.bedrock_reasoning_delta_count ?? 0) || 0,
        bedrockReasoningForwardedCount: Number(metadata.bedrock_reasoning_forwarded_count ?? story?.bedrock_reasoning_forwarded_count ?? 0) || 0,
        bedrockReasoningCharsReceived: Number(metadata.bedrock_reasoning_chars_received ?? story?.bedrock_reasoning_chars_received ?? 0) || 0,
        bedrockReasoningCharsForwarded: Number(metadata.bedrock_reasoning_chars_forwarded ?? story?.bedrock_reasoning_chars_forwarded ?? 0) || 0,
        bedrockReasoningEstimatedTokens: Number(metadata.bedrock_reasoning_estimated_tokens ?? story?.bedrock_reasoning_estimated_tokens ?? 0) || 0,
        bedrockReasoningEstimatedCostUsd: Number(
            metadata.bedrock_reasoning_estimated_cost_usd ?? story?.bedrock_reasoning_estimated_cost_usd ?? 0
        ) || 0,
        bedrockReasoningTokenSource: metadata.bedrock_reasoning_token_source ?? story?.bedrock_reasoning_token_source ?? "unavailable",
        streamInstrumentationVersion: Number(metadata.stream_instrumentation_version ?? story?.stream_instrumentation_version ?? 0) || 0,
        hasFinalEvent: Boolean(metadata.has_final_event ?? story?.has_final_event ?? false),
        streamCompleted: Boolean(metadata.stream_completed ?? story?.stream_completed ?? false),
        fromCache: Boolean(metadata.from_cache ?? story?.from_cache ?? false),
    };
}

function detectAnomalies(story) {
    const telemetry = getStoryTelemetry(story);
    const signals = [];
    if (story?.status === "error") signals.push("execution failed");
    if (telemetry.retryCount > 0) signals.push(`retried ${telemetry.retryCount}Ã—`);
    if (
        story?.service === "archimede" &&
        !telemetry.fromCache &&
        telemetry.streamCompleted &&
        telemetry.hasFinalEvent &&
        telemetry.streamInstrumentationVersion >= 2 &&
        telemetry.chunksEmitted === 0
    ) {
        signals.push("stream chunk telemetry reports 0 emitted chunks");
    }
    if (telemetry.preStreamDurationMs > 10000) signals.push(`slow pre-stream (${formatDurationMs(telemetry.preStreamDurationMs)})`);
    if (Number(story?.duration_ms || 0) > 60000) signals.push(`very slow (${formatDurationMs(story?.duration_ms || 0)})`);
    return signals;
}

function toDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function inRange(story, datePreset, customDateStart, customDateEnd) {
    const target = toDate(story.timestamp || story.ended_at || story.started_at);
    if (!target) return false;

    if (datePreset === "custom" && customDateStart && customDateEnd) {
        const start = new Date(customDateStart);
        const end = new Date(customDateEnd);
        return target >= start && target <= end;
    }

    const now = Date.now();
    const ago =
        datePreset === "today"
            ? 24 * 60 * 60 * 1000
            : datePreset === "30d"
              ? 30 * 24 * 60 * 60 * 1000
              : 7 * 24 * 60 * 60 * 1000;

    return target.getTime() >= now - ago;
}

function sumBy(list, selector) {
    return list.reduce((acc, item) => acc + Number(selector(item) || 0), 0);
}

function percentile(values, targetPercentile) {
    const sorted = values.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
    if (!sorted.length) return null;
    const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil((targetPercentile / 100) * sorted.length) - 1));
    return sorted[index];
}

function toCsv(rows) {
    if (!rows.length) return "";
    const headers = Object.keys(rows[0]);
    const escape = (value) => `"${String(value ?? "").replace(/"/g, "\"\"")}"`;
    return [headers.join(","), ...rows.map((row) => headers.map((h) => escape(row[h])).join(","))].join("\n");
}

function downloadText(filename, content, type = "application/json") {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
}

function KpiCard({ label, value, secondary }) {
    return (
        <div className="acc-kpi-card">
            <p className="acc-kpi-label">{label}</p>
            <p className="acc-kpi-value">{value}</p>
            {secondary ? <div className="acc-kpi-secondary">{secondary}</div> : null}
        </div>
    );
}

function StatusDot({ status }) {
    const color = STATUS_COLORS[status] || STATUS_COLORS.info;
    return <span className="acc-status-dot" style={{ backgroundColor: color }} aria-label={status} />;
}

function Badge({ children, color = "#8C8C89" }) {
    return (
        <span className="acc-badge" style={{ backgroundColor: `${color}22`, color }}>
            {children}
        </span>
    );
}

function Skeleton({ height = 120 }) {
    return <div className="acc-skeleton" style={{ height }} />;
}

function InfoTooltip({ title, description }) {
    return (
        <AntTooltip
            title={
                <div style={{ maxWidth: 320 }}>
                    {title && <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{title}</div>}
                    <div style={{ fontSize: 12, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>{description}</div>
                </div>
            }
            overlayInnerStyle={{
                background: 'rgba(15, 17, 24, 0.97)',
                borderRadius: 10,
                padding: '10px 14px',
                boxShadow: '0 8px 22px rgba(15, 23, 42, 0.38)',
            }}
        >
            <HelpCircle size={14} className="acc-info-icon" style={{ cursor: 'help', opacity: 0.5, transition: 'opacity 150ms' }} />
        </AntTooltip>
    );
}


function displayNAWhenEmpty(value, hasData) {
    return hasData ? value : "N.A";
}

function shortenId(value) {
    if (!value) return "n/a";
    const text = String(value);
    return text.length > 12 ? `${text.slice(0, 8)}...` : text;
}

function safeJsonParse(value) {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

function toUnixMs(value) {
    if (value == null) return null;
    if (typeof value === "number") return value > 9999999999 ? value : value * 1000;
    const date = new Date(value);
    const ms = date.getTime();
    return Number.isNaN(ms) ? null : ms;
}

function formatDurationMs(value, fallback = "N.A") {
    const num = Number(value);
    if (!Number.isFinite(num)) return fallback;

    const absNum = Math.abs(num);
    if (absNum < 1000) return `${Math.round(num)} ms`;

    const seconds = num / 1000;
    const absSeconds = Math.abs(seconds);
    if (absSeconds <= 60) {
        const maxFractionDigits = absSeconds < 10 ? 2 : 1;
        const roundedSeconds = Number(seconds.toFixed(maxFractionDigits));
        return `${roundedSeconds.toLocaleString("en-US", { maximumFractionDigits: maxFractionDigits })} s`;
    }

    const minutes = seconds / 60;
    const absMinutes = Math.abs(minutes);
    const maxFractionDigits = absMinutes < 10 ? 2 : 1;
    const roundedMinutes = Number(minutes.toFixed(maxFractionDigits));
    return `${roundedMinutes.toLocaleString("en-US", { maximumFractionDigits: maxFractionDigits })} min`;
}

function telemetryValueOrNA(value) {
    if (value == null || value === "") return "N.A";
    return formatDurationMs(value);
}

function getStoryCostUsd(story, costInputRate, costOutputRate) {
    const explicitCost = Number(story?.cost_usd ?? NaN);
    if (Number.isFinite(explicitCost) && explicitCost > 0) return explicitCost;

    const telemetry = getStoryTelemetry(story);
    const inputTokens = Number(story?.input_tokens || 0);
    const outputTokens = Number(story?.output_tokens || 0);
    const reasoningTokens = Number(telemetry.bedrockReasoningEstimatedTokens || 0);
    const visibleOutputTokens = Math.max(0, outputTokens - reasoningTokens);
    return (
        (inputTokens / 1000000) * Number(costInputRate || 0) +
        (visibleOutputTokens / 1000000) * Number(costOutputRate || 0) +
        Number(telemetry.bedrockReasoningEstimatedCostUsd || 0)
    );
}

function normalizeLogStatus(status) {
    if (!status) return "info";
    const normalized = String(status).toLowerCase();
    if (normalized === "failed") return "error";
    return normalized;
}

function parseSecurityEventsFromMessages(messages = []) {
    return messages
        .map((item, index) => {
            const message = typeof item === "string" ? item : item?.message;
            const parsed = safeJsonParse(message);
            if (parsed?.log_type !== "security_event") return null;
            return normalizeSecurityEvent(parsed, item, index);
        })
        .filter(Boolean);
}

function normalizeSecurityEvent(payload, sourceEvent = {}, index = 0) {
    const timestampMs = toUnixMs(payload?.timestamp) || Number(sourceEvent?.timestamp || 0) || null;
    const event = String(payload?.event || "security_event");
    const severity = String(payload?.severity || (event === "login_success" ? "info" : "warning")).toLowerCase();
    return {
        ...payload,
        event,
        severity,
        timestamp_ms: timestampMs,
        timestamp_display: timestampMs ? dayjs(timestampMs).format("YYYY-MM-DD HH:mm:ss") : "N.A",
        __key: `${payload?.request_id || sourceEvent?.eventId || "security"}-${timestampMs || index}-${index}`,
        __source_log_stream: sourceEvent?.logStreamName || payload?.log_stream || "auth-security",
    };
}

function securityEventTone(event, severity) {
    if (event === "login_success") return "success";
    if (event === "account_locked" || event === "suspicious_ip" || severity === "error") return "error";
    if (event === "login_failed") return "warning";
    return "info";
}

function buildSecurityIpStats(events) {
    const map = new Map();
    events.forEach((event) => {
        const ip = event.ip || "unknown";
        if (!map.has(ip)) {
            map.set(ip, {
                ip,
                total: 0,
                failed: 0,
                success: 0,
                locked: 0,
                unknownEmail: 0,
                domains: new Set(),
                lastSeenMs: 0,
            });
        }
        const row = map.get(ip);
        row.total += 1;
        if (event.event === "login_failed") row.failed += 1;
        if (event.event === "login_success") row.success += 1;
        if (event.event === "account_locked") row.locked += 1;
        if (event.reason === "unknown_email") row.unknownEmail += 1;
        if (event.email_domain) row.domains.add(event.email_domain);
        row.lastSeenMs = Math.max(row.lastSeenMs, Number(event.timestamp_ms || 0));
    });
    return Array.from(map.values())
        .map((row) => ({ ...row, domains: Array.from(row.domains) }))
        .sort((a, b) => b.locked - a.locked || b.failed - a.failed || b.total - a.total || b.lastSeenMs - a.lastSeenMs);
}

function toHumanChatText(value) {
    const text = String(value || "");
    return text
        .replace(/```[\s\S]*?```/g, (block) => block.replace(/```/g, ""))
        .replace(/^#{1,6}\s+/gm, "")
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/\*(.*?)\*/g, "$1")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/^\s*[-*]\s+/gm, "â€¢ ")
        .trim();
}

function getBackendQueryParams({ datePreset, customDateStart, customDateEnd }) {
    if (datePreset === "custom" && customDateStart && customDateEnd) {
        return {
            start_ts: Math.floor(new Date(customDateStart).getTime() / 1000),
            end_ts: Math.floor(new Date(customDateEnd).getTime() / 1000),
            limit: 600,
        };
    }

    if (datePreset === "today") return { hours: 24, limit: 600 };
    if (datePreset === "30d") return { hours: 24 * 30, limit: 900 };
    return { hours: 24 * 7, limit: 700 };
}

export default function ArchimedeControlCenter() {
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        activeView,
        datePreset,
        customDateStart,
        customDateEnd,
        selectedFeatures,
        status,
        search,
        selectedClient,
        selectedUser,
        selectedService,
        selectedModel,
        minDurationMs,
        maxDurationMs,
        minTokens,
        sortBy,
        setFilters,
        resetFilters,
    } = useArchimedeFilters();

    const [rawText, setRawText] = useState("");
    const [parsedData, setParsedData] = useState(() => ({
        executionStories: [],
        eventLogs: [],
        reviewFinalizeLogs: [],
        infoLogs: [],
        parseErrors: [],
        indexed: {
            byRequestId: new Map(),
            byUserId: new Map(),
            byClientId: new Map(),
            byFeature: new Map(),
            byService: new Map(),
        },
    }));
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [selectedRequestId, setSelectedRequestId] = useState("");
    const [activeClientId, setActiveClientId] = useState("");
    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
    const [historyTarget, setHistoryTarget] = useState(null);
    const [historyPage, setHistoryPage] = useState(1);
    const [darkMode, setDarkMode] = useState(false);
    const [costInputRate, setCostInputRate] = useState(1.75);
    const [costOutputRate, setCostOutputRate] = useState(14);
    const [showRawLog, setShowRawLog] = useState(false);
    const [chatInput, setChatInput] = useState("");
    const [chatMessages, setChatMessages] = useState([]);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [aiError, setAiError] = useState("");
    const [showDataStudio, setShowDataStudio] = useState(false);
    const [dataLoadError, setDataLoadError] = useState("");
    const [securityEvents, setSecurityEvents] = useState([]);
    const [securityLoadError, setSecurityLoadError] = useState("");
    const [clientDirectory, setClientDirectory] = useState([]);
    const [userDirectory, setUserDirectory] = useState([]);
    const [focusFeatureIndex, setFocusFeatureIndex] = useState(0);
    const [requestContextById, setRequestContextById] = useState({});
    const [isRequestContextLoading, setIsRequestContextLoading] = useState(false);
    const [requestContextError, setRequestContextError] = useState("");
    const [groupByAnalysis, setGroupByAnalysis] = useState(false);
    const [analysisGroupExpanded, setAnalysisGroupExpanded] = useState({});
    const [autoDiagStatus, setAutoDiagStatus] = useState("");
    const [aiChatOpen, setAiChatOpen] = useState(false);

    useEffect(() => {
        const next = {
            activeView: searchParams.get("view") || "control-room",
            datePreset: searchParams.get("range") || "7d",
            selectedFeatures: Array.from(new Set((searchParams.get("features") || "").split(",").filter(Boolean).map(normalizeFeature))),
            status: searchParams.get("status") || "all",
            search: searchParams.get("q") || "",
            selectedClient: searchParams.get("client") || "",
            selectedUser: searchParams.get("user") || "",
            selectedService: searchParams.get("service") || "all",
            selectedModel: searchParams.get("model") || "all",
            minDurationMs: searchParams.get("min_duration") || "",
            maxDurationMs: searchParams.get("max_duration") || "",
            minTokens: searchParams.get("min_tokens") || "",
            sortBy: searchParams.get("sort") || "latest",
            customDateStart: searchParams.get("from") || null,
            customDateEnd: searchParams.get("to") || null,
        };
        setFilters(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedFeaturesNormalized = useMemo(
        () => Array.from(new Set((selectedFeatures || []).map(normalizeFeature).filter(Boolean))),
        [selectedFeatures]
    );

    useEffect(() => {
        const params = new URLSearchParams();
        params.set("view", activeView);
        params.set("range", datePreset);
        params.set("status", status);
        if (selectedFeaturesNormalized.length) params.set("features", selectedFeaturesNormalized.join(","));
        if (search) params.set("q", search);
        if (selectedClient) params.set("client", selectedClient);
        if (selectedUser) params.set("user", selectedUser);
        if (selectedService !== "all") params.set("service", selectedService);
        if (selectedModel !== "all") params.set("model", selectedModel);
        if (minDurationMs) params.set("min_duration", String(minDurationMs));
        if (maxDurationMs) params.set("max_duration", String(maxDurationMs));
        if (minTokens) params.set("min_tokens", String(minTokens));
        if (sortBy !== "latest") params.set("sort", sortBy);
        if (customDateStart) params.set("from", customDateStart);
        if (customDateEnd) params.set("to", customDateEnd);
        setSearchParams(params, { replace: true });
    }, [
        activeView,
        datePreset,
        selectedFeaturesNormalized,
        status,
        search,
        selectedClient,
        selectedUser,
        selectedService,
        selectedModel,
        minDurationMs,
        maxDurationMs,
        minTokens,
        sortBy,
        customDateStart,
        customDateEnd,
        setSearchParams,
    ]);

    useEffect(() => {
        localStorage.setItem("archimedeControlCenter.theme", darkMode ? "dark" : "light");
    }, [darkMode]);

    useEffect(() => {
        const interval = window.setInterval(() => {
            handleRefresh(true);
        }, 30000);
        return () => window.clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawText]);

    useEffect(() => {
        let cancelled = false;
        async function loadDirectory() {
            try {
                const [clientsRes, usersRes] = await Promise.all([
                    api.get("clients/"),
                    api.get("users/"),
                ]);
                if (cancelled) return;
                setClientDirectory(Array.isArray(clientsRes?.data?.data) ? clientsRes.data.data : []);
                setUserDirectory(Array.isArray(usersRes?.data?.data) ? usersRes.data.data : []);
            } catch {
                if (!cancelled) {
                    setClientDirectory([]);
                    setUserDirectory([]);
                }
            }
        }
        loadDirectory();
        return () => {
            cancelled = true;
        };
    }, []);

    const normalizedStories = useMemo(() => {
        return parsedData.executionStories.map((story) => ({
            ...story,
            __normalized_feature: normalizeFeature(story.feature),
            __trace_key: getStoryTraceKey(story),
            __selection_key: getStorySelectionKey(story),
            __has_trace_id: Boolean(story.request_id),
        }));
    }, [parsedData.executionStories]);

    const allFeatures = useMemo(() => {
        const set = new Set(normalizedStories.map((item) => item.__normalized_feature || "other"));
        return Array.from(set);
    }, [normalizedStories]);

    const allServices = useMemo(() => {
        const set = new Set(normalizedStories.map((item) => item.service || "unknown"));
        return Array.from(set).sort();
    }, [normalizedStories]);

    const allModels = useMemo(() => {
        const set = new Set(normalizedStories.map((item) => item.model || "unknown"));
        return Array.from(set).sort();
    }, [normalizedStories]);

    const allClients = useMemo(() => {
        const set = new Set(normalizedStories.map((item) => item.client_id || "unknown_client"));
        return Array.from(set).sort();
    }, [normalizedStories]);

    const allUsers = useMemo(() => {
        const set = new Set(normalizedStories.map((item) => item.user_id || "unknown_user"));
        return Array.from(set).sort();
    }, [normalizedStories]);

    const clientLabelById = useMemo(() => {
        const map = new Map();
        clientDirectory.forEach((client) => {
            if (!client?.id) return;
            map.set(String(client.id), client.name || String(client.id));
        });
        return map;
    }, [clientDirectory]);

    const userLabelById = useMemo(() => {
        const map = new Map();
        userDirectory.forEach((user) => {
            if (!user?.id) return;
            const fullName = [user.name, user.surname].filter(Boolean).join(" ").trim();
            map.set(String(user.id), fullName || user.email || String(user.id));
        });
        return map;
    }, [userDirectory]);

    function getClientLabel(clientId) {
        if (!clientId) return "N.A";
        return clientLabelById.get(String(clientId)) || shortenId(clientId);
    }

    function getUserLabel(userId) {
        if (!userId) return "N.A";
        return userLabelById.get(String(userId)) || shortenId(userId);
    }

    const filteredStories = useMemo(() => {
        return normalizedStories
            .filter((story) => inRange(story, datePreset, customDateStart, customDateEnd))
            .filter((story) => (status === "all" ? true : story.status === status))
            .filter((story) => (selectedFeaturesNormalized.length ? selectedFeaturesNormalized.includes(story.__normalized_feature) : true))
            .filter((story) => (selectedService === "all" ? true : story.service === selectedService))
            .filter((story) => (selectedModel === "all" ? true : story.model === selectedModel))
            .filter((story) => (minDurationMs ? Number(story.duration_ms || 0) >= Number(minDurationMs) : true))
            .filter((story) => (maxDurationMs ? Number(story.duration_ms || 0) <= Number(maxDurationMs) : true))
            .filter((story) => (minTokens ? Number(story.total_tokens || 0) >= Number(minTokens) : true))
            .filter((story) => {
                if (!search) return true;
                const hay = `${story.request_id} ${story.execution_id} ${story.user_id} ${story.client_id} ${story.__normalized_feature}`.toLowerCase();
                return hay.includes(search.toLowerCase());
            })
            .filter((story) => (selectedClient ? story.client_id === selectedClient : true))
            .filter((story) => (selectedUser ? story.user_id === selectedUser : true))
            .sort((a, b) => {
                if (sortBy === "latency_desc") return Number(b.duration_ms || 0) - Number(a.duration_ms || 0);
                if (sortBy === "latency_asc") return Number(a.duration_ms || 0) - Number(b.duration_ms || 0);
                if (sortBy === "tokens_desc") return Number(b.total_tokens || 0) - Number(a.total_tokens || 0);
                return new Date(b.timestamp || 0) - new Date(a.timestamp || 0);
            });
    }, [
        normalizedStories,
        datePreset,
        customDateStart,
        customDateEnd,
        status,
        selectedFeaturesNormalized,
        selectedService,
        selectedModel,
        minDurationMs,
        maxDurationMs,
        minTokens,
        search,
        selectedClient,
        selectedUser,
        sortBy,
    ]);
    const hasLoadedData = filteredStories.length > 0;

    const filteredSecurityEvents = useMemo(() => {
        return securityEvents
            .filter((event) => inRange({ timestamp: event.timestamp || event.timestamp_ms }, datePreset, customDateStart, customDateEnd))
            .filter((event) => {
                if (!search) return true;
                const hay = [
                    event.event,
                    event.reason,
                    event.ip,
                    event.email_hash,
                    event.email_domain,
                    event.request_id,
                    event.user_id,
                    event.client_id,
                    event.user_agent,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return hay.includes(search.toLowerCase());
            })
            .sort((a, b) => Number(b.timestamp_ms || 0) - Number(a.timestamp_ms || 0));
    }, [securityEvents, datePreset, customDateStart, customDateEnd, search]);

    const securityStats = useMemo(() => {
        const total = filteredSecurityEvents.length;
        const failed = filteredSecurityEvents.filter((event) => event.event === "login_failed").length;
        const success = filteredSecurityEvents.filter((event) => event.event === "login_success").length;
        const locked = filteredSecurityEvents.filter((event) => event.event === "account_locked").length;
        const suspicious = filteredSecurityEvents.filter((event) => event.event === "suspicious_ip").length;
        const unknownEmail = filteredSecurityEvents.filter((event) => event.reason === "unknown_email").length;
        const ipStats = buildSecurityIpStats(filteredSecurityEvents);
        const suspiciousIpRows = ipStats.filter((row) => row.locked > 0 || row.failed >= 5 || row.unknownEmail >= 3);
        return {
            total,
            failed,
            success,
            locked,
            suspicious,
            unknownEmail,
            ipStats,
            suspiciousIpRows,
            failureRate: total ? (failed + locked) / total : 0,
        };
    }, [filteredSecurityEvents]);

    useEffect(() => {
        if (!parsedData.executionStories.length || filteredStories.length > 0) return;

        const hasRestrictiveFilters =
            selectedFeaturesNormalized.length > 0 ||
            status !== "all" ||
            Boolean(search) ||
            Boolean(selectedClient) ||
            Boolean(selectedUser) ||
            selectedService !== "all" ||
            selectedModel !== "all" ||
            Boolean(minDurationMs) ||
            Boolean(maxDurationMs) ||
            Boolean(minTokens) ||
            sortBy !== "latest";

        if (!hasRestrictiveFilters) return;

        setFilters({
            selectedFeatures: [],
            status: "all",
            search: "",
            selectedClient: "",
            selectedUser: "",
            selectedService: "all",
            selectedModel: "all",
            minDurationMs: "",
            maxDurationMs: "",
            minTokens: "",
            sortBy: "latest",
        });
    }, [
        parsedData.executionStories.length,
        filteredStories.length,
        selectedFeaturesNormalized.length,
        status,
        search,
        selectedClient,
        selectedUser,
        selectedService,
        selectedModel,
        minDurationMs,
        maxDurationMs,
        minTokens,
        sortBy,
        setFilters,
    ]);

    useEffect(() => {
        if (!parsedData.executionStories.length) return;

        const validClients = new Set(parsedData.executionStories.map((item) => item.client_id));
        const validUsers = new Set(parsedData.executionStories.map((item) => item.user_id));
        const validServices = new Set(parsedData.executionStories.map((item) => item.service));
        const validModels = new Set(parsedData.executionStories.map((item) => item.model));

        const patch = {};
        if (selectedClient && !validClients.has(selectedClient)) patch.selectedClient = "";
        if (selectedUser && !validUsers.has(selectedUser)) patch.selectedUser = "";
        if (selectedService !== "all" && !validServices.has(selectedService)) patch.selectedService = "all";
        if (selectedModel !== "all" && !validModels.has(selectedModel)) patch.selectedModel = "all";

        if (Object.keys(patch).length) {
            setFilters(patch);
        }
    }, [parsedData.executionStories, selectedClient, selectedUser, selectedService, selectedModel, setFilters]);

    const activeStory = useMemo(() => {
        if (!selectedRequestId) return filteredStories[0] || null;
        return filteredStories.find((item) => storyMatchesSelection(item, selectedRequestId)) || null;
    }, [filteredStories, selectedRequestId]);

    const kpis = useMemo(() => {
        const totalRequests = filteredStories.length;
        const successCount = filteredStories.filter((item) => item.status === "success").length;
        const totalTokens = sumBy(filteredStories, (item) => item.total_tokens);
        const inputTokens = sumBy(filteredStories, (item) => item.input_tokens);
        const outputTokens = sumBy(filteredStories, (item) => item.output_tokens);
        const archimedeStories = filteredStories.filter((item) => item.service === "archimede");
        const archimedeTotalTokens = sumBy(archimedeStories, (item) => item.total_tokens);
        const archimedeOutputTokens = sumBy(archimedeStories, (item) => item.output_tokens);
        const bedrockReasoningEstimatedTokens = sumBy(filteredStories, (item) => item.bedrock_reasoning_estimated_tokens);
        const bedrockReasoningEstimatedCost = sumBy(filteredStories, (item) => item.bedrock_reasoning_estimated_cost_usd);
        const visibleOutputTokens = Math.max(0, outputTokens - bedrockReasoningEstimatedTokens);
        const estimatedCost =
            (inputTokens / 1000000) * Number(costInputRate || 0) +
            (visibleOutputTokens / 1000000) * Number(costOutputRate || 0) +
            bedrockReasoningEstimatedCost;

        return {
            totalRequests,
            successRate: totalRequests ? (successCount / totalRequests) * 100 : 0,
            totalTokens,
            estimatedCost,
            bedrockReasoningEstimatedTokens,
            bedrockReasoningEstimatedCost,
            tokenEfficiencyPct: archimedeTotalTokens ? (archimedeOutputTokens / archimedeTotalTokens) * 100 : 0,
            archimedeTotalTokens,
            archimedeOutputTokens,
        };
    }, [filteredStories, costInputRate, costOutputRate]);

    const volumeData = useMemo(() => {
        const byKey = new Map();
        const useHourly = datePreset === "today";

        filteredStories.forEach((story) => {
            const date = toDate(story.timestamp);
            if (!date) return;
            const key = useHourly ? dayjs(date).format("MM-DD HH:00") : dayjs(date).format("MM-DD");
            const current = byKey.get(key) || { key, archimede: 0, "verdictia-backend": 0 };
            current[story.service] = Number(current[story.service] || 0) + 1;
            byKey.set(key, current);
        });

        return Array.from(byKey.values()).sort((a, b) => a.key.localeCompare(b.key));
    }, [filteredStories, datePreset]);

    const featureData = useMemo(() => {
        const counter = new Map();
        filteredStories.forEach((story) => {
            const feature = story.__normalized_feature || "other";
            counter.set(feature, Number(counter.get(feature) || 0) + 1);
        });
        return Array.from(counter.entries()).map(([name, value]) => ({ name, value }));
    }, [filteredStories]);

    const modelUsage = useMemo(() => {
        const counter = new Map();
        filteredStories.forEach((story) => {
            const model = story.model || "unknown";
            const tokens = Number(story.total_tokens || 0);
            counter.set(model, Number(counter.get(model) || 0) + tokens);
        });
        return Array.from(counter.entries()).map(([name, value]) => ({ name, value }));
    }, [filteredStories]);

    const latencyHistogram = useMemo(() => {
        const buckets = [
            { key: "0-1s", min: 0, max: 1000, count: 0 },
            { key: "1-2s", min: 1000, max: 2000, count: 0 },
            { key: "2-4s", min: 2000, max: 4000, count: 0 },
            { key: "4-8s", min: 4000, max: 8000, count: 0 },
            { key: "8s+", min: 8000, max: Number.POSITIVE_INFINITY, count: 0 },
        ];

        filteredStories.forEach((story) => {
            const duration = Number(story.duration_ms || 0);
            const bucket = buckets.find((item) => duration >= item.min && duration < item.max);
            if (bucket) bucket.count += 1;
        });

        return buckets.map((item) => ({ bucket: item.key, count: item.count }));
    }, [filteredStories]);

    const clientAggregates = useMemo(() => {
        const byClient = new Map();
        filteredStories.forEach((story) => {
            const key = story.client_id || "unknown_client";
            const current = byClient.get(key) || {
                client_id: key,
                requests: 0,
                tokens: 0,
                cost: 0,
                users: new Set(),
                last_active: null,
                stories: [],
            };
            current.requests += 1;
            current.tokens += Number(story.total_tokens || 0);
            current.cost +=
                (Number(story.input_tokens || 0) / 1000000) * Number(costInputRate || 0) +
                (Number(story.output_tokens || 0) / 1000000) * Number(costOutputRate || 0);
            current.users.add(story.user_id || "unknown_user");
            current.stories.push(story);
            const ts = new Date(story.timestamp);
            if (!current.last_active || ts > current.last_active) current.last_active = ts;
            byClient.set(key, current);
        });

        return Array.from(byClient.values()).map((item) => ({
            ...item,
            unique_users: item.users.size,
            users: undefined,
        }));
    }, [filteredStories, costInputRate, costOutputRate]);

    const activeClientUsers = useMemo(() => {
        if (!activeClientId) return [];
        const stories = filteredStories.filter((item) => item.client_id === activeClientId);
        const byUser = new Map();
        stories.forEach((story) => {
            const key = story.user_id || "unknown_user";
            const current = byUser.get(key) || {
                user_id: key,
                requests: 0,
                tokens: 0,
                avg_latency_ms: 0,
                total_latency: 0,
                features: new Set(),
                last_active: null,
                stories: [],
            };
            current.requests += 1;
            current.tokens += Number(story.total_tokens || 0);
            current.total_latency += Number(story.duration_ms || 0);
            current.features.add(story.__normalized_feature || "other");
            current.stories.push(story);
            const ts = new Date(story.timestamp);
            if (!current.last_active || ts > current.last_active) current.last_active = ts;
            byUser.set(key, current);
        });

        return Array.from(byUser.values()).map((item) => ({
            ...item,
            avg_latency_ms: item.requests ? Math.round(item.total_latency / item.requests) : 0,
            features: Array.from(item.features),
        }));
    }, [activeClientId, filteredStories]);

    const activeClientAggregate = useMemo(() => {
        if (!clientAggregates.length) return null;
        if (!activeClientId) return clientAggregates[0];
        return clientAggregates.find((item) => item.client_id === activeClientId) || clientAggregates[0];
    }, [clientAggregates, activeClientId]);

    useEffect(() => {
        if (selectedClient) {
            setActiveClientId(selectedClient);
            return;
        }
        if (!activeClientId && clientAggregates.length) {
            setActiveClientId(clientAggregates[0].client_id);
        }
    }, [selectedClient, activeClientId, clientAggregates]);

    const liveTail = useMemo(() => filteredStories.slice(0, 20), [filteredStories]);

    const requestEvents = useMemo(() => {
        if (!activeStory) return [];
        return parsedData.eventLogs.filter((item) => item.request_id === activeStory.request_id);
    }, [parsedData.eventLogs, activeStory]);

    useEffect(() => {
        const requestId = activeStory?.request_id;
        if (!requestId) return undefined;

        let cancelled = false;
        async function loadRequestContext() {
            setIsRequestContextLoading(true);
            setRequestContextError("");
            try {
                const [verdictiaRes, archimedeRes] = await Promise.allSettled([
                    fetchObservabilityRequestLogs(requestId, { hours: 24 * 30, limit: 600, log_group: "verdictia-backend" }),
                    fetchObservabilityRequestLogs(requestId, { hours: 24 * 30, limit: 600, log_group: "archimede" }),
                ]);

                const collectEvents = (result, source) =>
                    result.status === "fulfilled" && Array.isArray(result.value?.data)
                        ? result.value.data.map((item) => ({ ...item, source_log_group: source }))
                        : [];

                const rawEvents = [...collectEvents(verdictiaRes, "verdictia-backend"), ...collectEvents(archimedeRes, "archimede")];
                const parsedBlocks = rawEvents
                    .map((event) => {
                        const parsed = safeJsonParse(event?.message || "");
                        if (!parsed || typeof parsed !== "object") return null;
                        return {
                            ...parsed,
                            __source_log_group: event.source_log_group,
                            __event_timestamp_ms: toUnixMs(event.timestamp),
                        };
                    })
                    .filter(Boolean);

                const executionStories = parsedBlocks.filter((item) => item.log_type === "execution_story");
                const eventLogs = parsedBlocks
                    .filter((item) => isEventLikeLogType(item.log_type))
                    .map((item) => ({
                        ...item,
                        log_type: "event_log",
                        ts: item.ts || item.timestamp || item.__event_timestamp_ms,
                        status: normalizeLogStatus(item?.data?.status || item.status),
                        type: item.type || item.event || item.log_type || "event",
                        step: item.step || item.event || item.log_type || "event",
                    }));
                const reviewFinalizeLogs = parsedBlocks.filter((item) => item.log_type === "review_finalize");
                const errorMessage =
                    (verdictiaRes.status === "rejected" && verdictiaRes.reason?.response?.data?.message) ||
                    (archimedeRes.status === "rejected" && archimedeRes.reason?.response?.data?.message) ||
                    "";

                if (cancelled) return;
                setRequestContextById((prev) => ({
                    ...prev,
                    [requestId]: {
                        rawEvents,
                        parsedBlocks,
                        executionStories,
                        eventLogs,
                        reviewFinalizeLogs,
                    },
                }));
                setRequestContextError(errorMessage);
            } catch (error) {
                if (cancelled) return;
                setRequestContextError(error?.response?.data?.message || error.message || "Unable to load request context");
            } finally {
                if (!cancelled) setIsRequestContextLoading(false);
            }
        }

        loadRequestContext();
        return () => {
            cancelled = true;
        };
    }, [activeStory?.request_id]);

    const requestContext = useMemo(() => {
        if (!activeStory?.request_id) return null;
        return requestContextById[activeStory.request_id] || null;
    }, [activeStory, requestContextById]);
    const isInspectorContextBootstrapping = Boolean(activeStory?.request_id) && isRequestContextLoading && !requestContext;

    const requestExecutionStories = useMemo(() => {
        if (!activeStory) return [];
        const contextStories = Array.isArray(requestContext?.executionStories) ? requestContext.executionStories : [];
        const merged = [activeStory, ...contextStories];
        const seen = new Set();
        return merged.filter((item) => {
            const key = `${item.execution_id || "no_exec"}:${item.service || "unknown"}:${item.started_at || item.timestamp || ""}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [activeStory, requestContext]);

    const requestEventLogsMerged = useMemo(() => {
        const contextEvents = Array.isArray(requestContext?.eventLogs) ? requestContext.eventLogs : [];
        if (!contextEvents.length) return requestEvents;
        return contextEvents;
    }, [requestContext, requestEvents]);

    const requestReviewFinalizeLogs = useMemo(() => {
        const contextFinalizeLogs = Array.isArray(requestContext?.reviewFinalizeLogs)
            ? requestContext.reviewFinalizeLogs
            : [];
        if (contextFinalizeLogs.length) return contextFinalizeLogs;
        return (parsedData.reviewFinalizeLogs || []).filter((item) => item.request_id === activeStory?.request_id);
    }, [requestContext, parsedData.reviewFinalizeLogs, activeStory?.request_id]);

    const requestTimelineFeed = useMemo(() => {
        if (!activeStory) return [];

        const rootStartMs = toUnixMs(activeStory.started_at) || toUnixMs(activeStory.timestamp);
        const lines = [];

        lines.push({
            kind: "main_request",
            timestamp_ms: rootStartMs,
            label: "Main request received",
            detail: `${inferOperationType(activeStory)} on ${activeStory.service || "unknown service"}`,
            status: activeStory.status || "info",
            service: activeStory.service || "unknown",
        });

        (activeStory.steps || []).forEach((step) => {
            lines.push({
                kind: "step",
                timestamp_ms: toUnixMs(step.started_at),
                label: step.step || "step",
                detail: formatDurationMs(step.duration_ms ?? 0),
                status: step.status || "info",
                service: activeStory.service || "unknown",
            });
        });

        requestExecutionStories.forEach((story) => {
            if (story.execution_id === activeStory.execution_id && story.service === activeStory.service) return;
            lines.push({
                kind: "sub_request",
                timestamp_ms: toUnixMs(story.started_at || story.timestamp),
                label: `Sub-request ${story.service || "unknown"}`,
                detail: `${story.__normalized_feature || normalizeFeature(story.feature) || "other"} | ${formatDurationMs(story.duration_ms ?? 0)}`,
                status: story.status || "info",
                service: story.service || "unknown",
            });
        });

        requestEventLogsMerged.forEach((event) => {
            lines.push({
                kind: "event_log",
                timestamp_ms: toUnixMs(event.ts || event.timestamp) || event.__event_timestamp_ms || null,
                label: event.type || event.step || "event",
                detail: `${event.step || "unknown step"}${event.data?.duration_ms != null ? ` | ${formatDurationMs(event.data.duration_ms)}` : ""}`,
                status: event.status || event.data?.status || "info",
                service: event.service || event.__source_log_group || "unknown",
            });
        });

        requestReviewFinalizeLogs.forEach((event) => {
            lines.push({
                kind: "review_finalize",
                timestamp_ms: toUnixMs(event.timestamp) || event.__event_timestamp_ms || null,
                label: event.finalize_outcome || event.event || "review finalize",
                detail: `${event.finalize_reason || "unknown"} | ${event.criterion_ids?.length || 0} criteria`,
                status: event.event === "stream_error_forced_done" ? "error" : "info",
                service: event.service || event.__source_log_group || "verdictia-backend",
            });
        });

        return lines
            .filter((item) => item.timestamp_ms != null)
            .sort((a, b) => a.timestamp_ms - b.timestamp_ms)
            .map((item) => ({
                ...item,
                offset_ms: rootStartMs != null ? Math.max(0, item.timestamp_ms - rootStartMs) : null,
            }));
    }, [activeStory, requestExecutionStories, requestEventLogsMerged, requestReviewFinalizeLogs]);

    const requestNarrative = useMemo(() => {
        if (!activeStory) return null;
        const services = Array.from(new Set(requestExecutionStories.map((item) => item.service).filter(Boolean)));
        return {
            title: `${inferOperationType(activeStory)} request`,
            mainNeed: `Backend needed to execute ${activeStory.__normalized_feature || normalizeFeature(activeStory.feature) || "analysis"} and return a ${activeStory.status || "unknown"} outcome.`,
            flow: `Flow crossed ${services.length || 1} service(s): ${services.join(" -> ") || activeStory.service || "unknown"}.`,
            stepsCount: (activeStory.steps || []).length,
            eventsCount: requestEventLogsMerged.length,
            finalizeEventsCount: requestReviewFinalizeLogs.length,
        };
    }, [activeStory, requestExecutionStories, requestEventLogsMerged, requestReviewFinalizeLogs]);

    function applyParsed(parsed) {
        setParsedData({
            ...parsed,
            executionStories: parsed.executionStories,
        });
        if (!selectedRequestId && parsed.executionStories.length) {
            const first = parsed.executionStories[0];
            setSelectedRequestId(getStorySelectionKey(first));
        }
    }

    async function handleRefresh(silent = false) {
        if (!silent) setIsRefreshing(true);
        await new Promise((resolve) => window.setTimeout(resolve, silent ? 180 : 450));
        try {
            if (rawText.trim()) {
                const parsed = parseObservabilityLogs(rawText);
                applyParsed(parsed);
                setSecurityEvents(parseSecurityEventsFromMessages(rawText.split(/\r?\n/)));
                setSecurityLoadError("");
                setDataLoadError("");
            } else {
                const params = getBackendQueryParams({ datePreset, customDateStart, customDateEnd });
                const [verdictiaRes, archimedeRes, securityRes] = await Promise.allSettled([
                    fetchObservabilityStories({ ...params, log_group: "verdictia-backend" }),
                    fetchObservabilityStories({ ...params, log_group: "archimede" }),
                    fetchObservabilitySecurityEvents(params),
                ]);

                const collectMessages = (result) =>
                    result.status === "fulfilled" && Array.isArray(result.value?.data)
                        ? result.value.data.map((item) => item?.message).filter(Boolean)
                        : [];

                const messages = [...collectMessages(verdictiaRes), ...collectMessages(archimedeRes)];
                const parsed = parseObservabilityLogs(messages.join("\n"));
                applyParsed(parsed);
                if (!messages.length && (verdictiaRes.status === "rejected" || archimedeRes.status === "rejected")) {
                    const err =
                        (verdictiaRes.status === "rejected" && verdictiaRes.reason?.response?.data?.message) ||
                        (archimedeRes.status === "rejected" && archimedeRes.reason?.response?.data?.message) ||
                        "Unable to load observability data";
                    setDataLoadError(err);
                } else {
                    setDataLoadError("");
                }

                if (securityRes.status === "fulfilled" && Array.isArray(securityRes.value?.data)) {
                    setSecurityEvents(parseSecurityEventsFromMessages(securityRes.value.data));
                    setSecurityLoadError("");
                } else {
                    setSecurityEvents([]);
                    setSecurityLoadError(securityRes.reason?.response?.data?.message || securityRes.reason?.message || "Unable to load security events");
                }
            }
        } catch (error) {
            setParsedData((prev) => ({ ...prev, executionStories: [] }));
            setSecurityEvents([]);
            setDataLoadError(error?.response?.data?.message || error.message || "Unable to load observability data");
        }
        if (!silent) setIsRefreshing(false);
    }

    function onFileUpload(event) {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const text = String(reader.result || "");
            setRawText(text);
            const parsed = parseObservabilityLogs(text);
            applyParsed(parsed);
            setSecurityEvents(parseSecurityEventsFromMessages(text.split(/\r?\n/)));
        };
        reader.readAsText(file);
    }

    function toggleFeatureFilter(feature) {
        const normalized = normalizeFeature(feature);
        const exists = selectedFeaturesNormalized.includes(normalized);
        setFilters({
            selectedFeatures: exists
                ? selectedFeaturesNormalized.filter((item) => item !== normalized)
                : [...selectedFeaturesNormalized, normalized],
        });
    }

    function exportVisibleAsJson() {
        downloadText("archimede-control-center-visible.json", JSON.stringify(filteredStories, null, 2), "application/json");
    }

    function exportVisibleAsCsv() {
        const rows = filteredStories.map((story) => ({
            timestamp: story.timestamp,
            request_id: story.request_id,
            execution_id: story.execution_id,
            service: story.service,
            feature: story.__normalized_feature || normalizeFeature(story.feature),
            status: story.status,
            user_id: story.user_id,
            client_id: story.client_id,
            duration_ms: story.duration_ms,
            input_tokens: story.input_tokens,
            output_tokens: story.output_tokens,
            total_tokens: story.total_tokens,
            bedrock_reasoning_estimated_tokens: story.bedrock_reasoning_estimated_tokens,
            bedrock_reasoning_estimated_cost_usd: story.bedrock_reasoning_estimated_cost_usd,
            bedrock_reasoning_delta_count: story.bedrock_reasoning_delta_count,
            bedrock_reasoning_forwarded_count: story.bedrock_reasoning_forwarded_count,
            cost_usd: Number(story.cost_usd || 0).toFixed(6),
        }));
        downloadText("archimede-control-center-visible.csv", toCsv(rows), "text/csv;charset=utf-8;");
    }

    function copyRequestId(requestId) {
        navigator.clipboard.writeText(requestId || "");
    }

function buildHumanTraceabilityPrompt(story, userNeed = "") {
        const need = String(userNeed || "").trim();
        const defaultNeed =
            "Explain what happened in human language (functional, not overly technical), with traceability from logs.";
        return `${need || defaultNeed}

Write for a product owner / operations human (plain language, short sentences).
Use this structure:
1) What happened (business view)
2) User impact (what the user likely experienced)
3) Timeline with traceability
4) Why it happened (most likely cause)
5) Next actions (3 concrete checks/fixes max)

Traceability rules:
- For each important claim, include a short evidence tag with concrete data from the trace.
- Example evidence tags: [service:archimede] [step:streaming 22450ms] [status:error] [request_id:${story?.request_id || "none"}]
- If evidence is missing, explicitly say "unknown from logs".

Non-negotiable interpretation rules:
- Do not conclude "empty stream" or "user saw nothing" from chunks_emitted=0 unless stream_instrumentation_version >= 2 is explicit.
- If stream_instrumentation_version is missing/legacy, treat chunks_emitted as potentially unavailable.
- For backend proxy services (service=verdictia-backend), llm_call_count=0 and models_used=[] are expected; do not flag as failure.
- For TTFT, prefer ttft_upstream_ms when available.
- If only time_to_first_token_ms=0 is present with legacy/missing instrumentation, treat it as inconclusive instrumentation, not as a performance fact.

Tone:
- Be clear and practical.
- No jargon unless necessary.
- No code.
- Keep it useful for decision making.

Language constraints:
- Reply only in French.
- Do not use English words or section titles unless they are quoted log fields.
- If a source label is in English, explain it in French.

Formatting constraints:
- Return plain text only.
- Do not use markdown symbols like #, *, **, or code fences.
- Use short lines and readable bullets with "-" only.`;
    }

    async function askAiExplanation(followup = "", options = {}) {
        if (!activeStory) return;
        setAiChatOpen(true);

        setIsAiLoading(true);
        setAiError("");

        const {
            messageRole = "user",
            visibleText = followup,
        } = options;
        const safeFollowup = String(followup || "").trim();
        const formattingGuard = `Formatting constraints:
- Return plain text only.
- Do not use markdown symbols like #, *, **, or code fences.
- Keep structure readable with short lines and "-" bullets.`;
        const basePrompt = safeFollowup || buildHumanTraceabilityPrompt(activeStory);
        const effectivePrompt = `${basePrompt}\n\n${formattingGuard}`;
        const safeVisibleText = String(visibleText || "").trim() || "Explain this request in human language with traceability.";

        const previous = [...chatMessages];
        const nextMessages = [...previous];

        if (effectivePrompt) {
            nextMessages.push({ role: messageRole, text: safeVisibleText, severity: options?.severity });
            setChatMessages(nextMessages);
        }

        let assistantText = "";
        const withAssistantPlaceholder = [...nextMessages, { role: "assistant", text: "" }];
        setChatMessages(withAssistantPlaceholder);

        try {
            const aiStoryContext = {
                main_execution_story: activeStory,
                related_execution_stories: requestExecutionStories,
                event_logs: requestEventLogsMerged,
                raw_cloudwatch_events: requestContext?.rawEvents || [],
                timeline_feed: requestTimelineFeed,
                telemetry: {
                    time_to_first_token_ms: activeStoryTelemetry.timeToFirstTokenMs,
                    ttft_control_ms: Number.isFinite(activeStoryTelemetry.ttftControlMs) ? activeStoryTelemetry.ttftControlMs : null,
                    ttft_upstream_ms: activeStoryTelemetry.ttftUpstreamMs,
                    pre_stream_duration_ms: activeStoryTelemetry.preStreamDurationMs,
                    total_stream_duration_ms: activeStoryTelemetry.totalStreamDurationMs,
                    chunks_emitted: activeStoryTelemetry.chunksEmitted,
                    has_final_event: activeStoryTelemetry.hasFinalEvent,
                    stream_completed: activeStoryTelemetry.streamCompleted,
                    stream_instrumentation_version: activeStoryTelemetry.streamInstrumentationVersion,
                    from_cache: activeStoryTelemetry.fromCache,
                },
            };
            await streamOpenAiExplanation({
                storyJson: aiStoryContext,
                followUpPrompt: effectivePrompt,
                messages: nextMessages.map((msg) => ({ role: msg.role === "assistant" ? "assistant" : "user", content: msg.text })),
                onDelta: (delta) => {
                    assistantText += delta;
                    setChatMessages((current) => {
                        const updated = [...current];
                        updated[updated.length - 1] = { role: "assistant", text: assistantText };
                        return updated;
                    });
                },
            });
            return { ok: true };
        } catch (error) {
            setAiError(error.message || "Unexpected AI explain error");
            return { ok: false, error: error?.message || "Unexpected AI explain error" };
        } finally {
            setIsAiLoading(false);
        }
    }

    const userHistoryStories = useMemo(() => {
        if (!historyTarget?.stories) return [];
        return historyTarget.stories;
    }, [historyTarget]);

    const historyPageSize = 8;
    const historyPaginated = useMemo(() => {
        const start = (historyPage - 1) * historyPageSize;
        return userHistoryStories.slice(start, start + historyPageSize);
    }, [userHistoryStories, historyPage]);

    useEffect(() => {
        if (!rawText.trim()) {
            handleRefresh(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datePreset, customDateStart, customDateEnd]);

    const avgLatencyMs = useMemo(() => {
        if (!filteredStories.length) return 0;
        return Math.round(sumBy(filteredStories, (item) => item.duration_ms) / filteredStories.length);
    }, [filteredStories]);

    const topFeature = useMemo(() => {
        if (!featureData.length) return "n/a";
        const sorted = [...featureData].sort((a, b) => b.value - a.value);
        return sorted[0]?.name || "n/a";
    }, [featureData]);

    const sortedFeatures = useMemo(() => {
        return [...featureData].sort((a, b) => b.value - a.value);
    }, [featureData]);

    const focusedFeature = useMemo(() => {
        if (!sortedFeatures.length) return null;
        const safeIndex = Math.min(focusFeatureIndex, sortedFeatures.length - 1);
        return sortedFeatures[safeIndex];
    }, [sortedFeatures, focusFeatureIndex]);

    const focusedFeatureCost = useMemo(() => {
        if (!focusedFeature?.name) return 0;
        return filteredStories
            .filter((story) => story.__normalized_feature === focusedFeature.name)
            .reduce((acc, story) => acc + getStoryCostUsd(story, costInputRate, costOutputRate), 0);
    }, [filteredStories, focusedFeature, costInputRate, costOutputRate]);

    const focusedFeatureTokenUsage = useMemo(() => {
        if (!focusedFeature?.name) return { input: 0, output: 0, total: 0 };
        const stories = filteredStories.filter((story) => story.__normalized_feature === focusedFeature.name);
        return {
            input: sumBy(stories, (story) => story.input_tokens),
            output: sumBy(stories, (story) => story.output_tokens),
            total: sumBy(stories, (story) => story.total_tokens),
        };
    }, [filteredStories, focusedFeature]);

    const thinkingSignalsCount = useMemo(() => {
        const thinkingEventCount = (parsedData.eventLogs || []).filter((item) =>
            String(item.type || item.step || "").toLowerCase().startsWith("thinking_")
        ).length;
        if (thinkingEventCount > 0) return thinkingEventCount;

        const traces = new Set();
        filteredStories.forEach((story) => {
            const hasReasoningSignal = Boolean(
                Number(story.bedrock_reasoning_delta_count || 0) > 0
                || Number(story.bedrock_reasoning_estimated_tokens || 0) > 0
                || story.bedrock_reasoning_enabled
            );
            if (!hasReasoningSignal) return;
            traces.add(story.request_id || story.execution_id || getStoryTraceKey(story));
        });
        return traces.size;
    }, [parsedData.eventLogs, filteredStories]);

    const totalThinkingUsage = useMemo(() => {
        return filteredStories.reduce(
            (acc, story) => {
                const telemetry = getStoryTelemetry(story);
                acc.deltas += Number(telemetry.bedrockReasoningDeltaCount || 0);
                acc.tokens += Number(telemetry.bedrockReasoningEstimatedTokens || 0);
                acc.cost += Number(telemetry.bedrockReasoningEstimatedCostUsd || 0);
                return acc;
            },
            { deltas: 0, tokens: 0, cost: 0 }
        );
    }, [filteredStories]);

    const focusedThinkingUsage = useMemo(() => {
        if (!focusedFeature?.name) return { deltas: 0, tokens: 0, cost: 0 };
        return filteredStories
            .filter((story) => story.__normalized_feature === focusedFeature.name)
            .reduce(
                (acc, story) => {
                    const telemetry = getStoryTelemetry(story);
                    acc.deltas += Number(telemetry.bedrockReasoningDeltaCount || 0);
                    acc.tokens += Number(telemetry.bedrockReasoningEstimatedTokens || 0);
                    acc.cost += Number(telemetry.bedrockReasoningEstimatedCostUsd || 0);
                    return acc;
                },
                { deltas: 0, tokens: 0, cost: 0 }
            );
    }, [filteredStories, focusedFeature]);

    useEffect(() => {
        if (!sortedFeatures.length) {
            setFocusFeatureIndex(0);
            return;
        }
        if (focusFeatureIndex > sortedFeatures.length - 1) {
            setFocusFeatureIndex(0);
        }
    }, [sortedFeatures, focusFeatureIndex]);

    const uniqueActiveUsers = useMemo(() => {
        return new Set(filteredStories.map((item) => item.user_id)).size;
    }, [filteredStories]);

    const activeStoryTelemetry = useMemo(() => (activeStory ? getStoryTelemetry(activeStory) : getStoryTelemetry(null)), [activeStory]);

    const streamingAnomalyBadges = useMemo(() => {
        if (!activeStory) return [];
        const badges = [];
        const ttft = activeStoryTelemetry.timeToFirstTokenMs;
        const chunks = activeStoryTelemetry.chunksEmitted;
        const hasStreamSignals = activeStoryTelemetry.hasFinalEvent || activeStoryTelemetry.streamCompleted;
        const hasV2Telemetry = activeStoryTelemetry.streamInstrumentationVersion >= 2;
        if (!activeStoryTelemetry.fromCache && hasStreamSignals && chunks === 0 && hasV2Telemetry) {
            badges.push({
                kind: "warning",
                text: "âš  chunk telemetry reports 0 emitted chunks â€” verify stream writer instrumentation",
            });
        } else if (!activeStoryTelemetry.fromCache && hasStreamSignals && chunks === 0 && !hasV2Telemetry) {
            badges.push({
                kind: "warning",
                text: "âš  chunk telemetry missing on legacy instrumentation â€” avoid empty-stream conclusions",
            });
        }
        if (ttft === 0 && chunks > 0 && hasV2Telemetry) {
            badges.push({
                kind: "warning",
                text: "âš  TTFT at 0 ms with emitted chunks â€” check first-token measurement source",
            });
        }
        if (activeStoryTelemetry.preStreamDurationMs > 10000) {
            badges.push({
                kind: "warning",
                text: `âš  slow pre-stream (${formatDurationMs(activeStoryTelemetry.preStreamDurationMs)}) â€” check upstream data fetch`,
            });
        }
        if (activeStoryTelemetry.totalStreamDurationMs > 60000) {
            badges.push({
                kind: "warning",
                text: `âš  long stream (${formatDurationMs(activeStoryTelemetry.totalStreamDurationMs)}) â€” may indicate LLM timeout risk`,
            });
        }
        if (activeStoryTelemetry.retryCount > 0) {
            badges.push({
                kind: "error",
                text: `âœ— retried ${activeStoryTelemetry.retryCount}Ã— â€” check for upstream instability`,
            });
        }
        if (activeStoryTelemetry.llmCallCount === 0 && activeStory.service === "archimede" && !activeStoryTelemetry.fromCache) {
            badges.push({
                kind: "warning",
                text: "âš  no LLM call recorded â€” possible non-LLM stream path or instrumentation issue",
            });
        }
        return badges;
    }, [activeStory, activeStoryTelemetry]);

    const hasBugStorySignal = useMemo(() => {
        if (!activeStory) return false;
        return activeStory.status === "error" || streamingAnomalyBadges.length > 0;
    }, [activeStory, streamingAnomalyBadges]);

    const bugStorySeverity = activeStory?.status === "error" ? "error" : "warning";

    const reasoningTelemetryVisible = useMemo(() => {
        if (!activeStory) return false;
        const t = activeStoryTelemetry;
        return Boolean(
            t.bedrockReasoningEnabled
            || t.bedrockReasoningBudgetTokens > 0
            || t.bedrockReasoningDeltaCount > 0
            || t.bedrockReasoningEstimatedTokens > 0
            || t.bedrockReasoningEstimatedCostUsd > 0
            || t.bedrockReasoningCharsReceived > 0
            || t.bedrockReasoningCharsForwarded > 0
        );
    }, [activeStory, activeStoryTelemetry]);

    const outputSplit = useMemo(() => {
        const totalOut = Number(activeStory?.output_tokens || 0);
        const totalCost = Number(activeStory?.cost_usd || 0);
        const reasoningTokens = Number(activeStoryTelemetry.bedrockReasoningEstimatedTokens || 0);
        const reasoningCost = Number(activeStoryTelemetry.bedrockReasoningEstimatedCostUsd || 0);

        return {
            totalOut,
            visibleOut: Math.max(0, totalOut - reasoningTokens),
            reasoningTokens,
            totalCost,
            visibleCost: Math.max(0, totalCost - reasoningCost),
            reasoningCost,
        };
    }, [activeStory, activeStoryTelemetry]);

    const outputRichness = useMemo(() => {
        if (!activeStory) return [];
        const telemetry = activeStoryTelemetry;
        const chips = [];
        if (telemetry.cardsEmitted != null) chips.push({ label: `${formatNumber(telemetry.cardsEmitted)} cards` });
        if (telemetry.chunksEmitted != null) chips.push({ label: `${formatNumber(telemetry.chunksEmitted)} chunks` });
        if (telemetry.contextsCount != null) chips.push({ label: `${formatNumber(telemetry.contextsCount)} contexts` });
        if (telemetry.jurisdictionsCount != null) chips.push({ label: `${formatNumber(telemetry.jurisdictionsCount)} jurisdictions` });
        if (telemetry.completionTokens != null) chips.push({ label: `${formatNumber(telemetry.completionTokens)} completion tokens` });
        if (reasoningTelemetryVisible) {
            chips.push({ label: `${formatNumber(telemetry.bedrockReasoningEstimatedTokens)} Bedrock reasoning tokens` });
        }
        return chips;
    }, [activeStory, activeStoryTelemetry, reasoningTelemetryVisible]);

    const streamBreakdown = useMemo(() => {
        const totalDuration = Math.max(1, Number(activeStory?.duration_ms || 0));
        const preStream = Math.max(0, activeStoryTelemetry.preStreamDurationMs);
        const streamTotal = Math.max(preStream, activeStoryTelemetry.totalStreamDurationMs);
        const streamOnly = Math.max(0, streamTotal - preStream);
        const prePct = Math.min(100, (preStream / totalDuration) * 100);
        const streamPct = Math.min(Math.max(100 - prePct, 0), (streamOnly / totalDuration) * 100);
        return {
            totalDuration,
            preStream,
            streamOnly,
            prePct,
            streamPct,
        };
    }, [activeStory?.duration_ms, activeStoryTelemetry]);

    const requestTraceStories = useMemo(() => {
        if (!activeStory?.request_id) return [];
        return requestExecutionStories.filter((story) => story.request_id && story.request_id === activeStory.request_id);
    }, [activeStory, requestExecutionStories]);

    const dualServiceTrace = useMemo(() => {
        if (!activeStory?.request_id) return null;
        const traceStories = requestTraceStories.length ? requestTraceStories : [activeStory];
        const gatewayStory = traceStories.find((story) => story.service === "verdictia-backend") || null;
        const archimedeStory = traceStories.find((story) => story.service === "archimede") || null;
        const allStartCandidates = traceStories.map((story) => toUnixMs(story.started_at || story.timestamp)).filter((ms) => ms != null);
        const allEndCandidates = traceStories
            .map((story) => {
                const endMs = toUnixMs(story.ended_at);
                if (endMs != null) return endMs;
                const startMs = toUnixMs(story.started_at || story.timestamp);
                if (startMs == null) return null;
                return startMs + Number(story.duration_ms || 0);
            })
            .filter((ms) => ms != null);
        if (!allStartCandidates.length) return null;
        const traceStart = Math.min(...allStartCandidates);
        const traceEnd = allEndCandidates.length ? Math.max(...allEndCandidates) : traceStart + Math.max(1, Number(activeStory.duration_ms || 0));
        const totalWallMs = Math.max(1, traceEnd - traceStart);

        function buildBars(story) {
            if (!story) return [];
            const storyStart = toUnixMs(story.started_at || story.timestamp) || traceStart;
            const steps = Array.isArray(story.steps) && story.steps.length ? story.steps : [{ step: story.__normalized_feature || "execution", duration_ms: story.duration_ms, status: story.status, started_at: story.started_at }];
            return steps.map((step, idx) => {
                const stepStartRaw = toUnixMs(step.started_at);
                const stepDuration = Math.max(1, Number(step.duration_ms || 1));
                const stepStart = stepStartRaw != null ? stepStartRaw : storyStart;
                const left = Math.max(0, ((stepStart - traceStart) / totalWallMs) * 100);
                const width = Math.max(1.2, (stepDuration / totalWallMs) * 100);
                return {
                    key: `${story.execution_id || story.request_id}-${step.step}-${idx}`,
                    label: step.step || "step",
                    status: step.status || "info",
                    duration: stepDuration,
                    left,
                    width,
                    color: STEP_COLORS[step.step] || SERVICE_COLORS[story.service] || "#0B3D91",
                    startMs: stepStart,
                    endMs: stepStart + stepDuration,
                };
            });
        }

        const gatewayBars = buildBars(gatewayStory);
        const archimedeBars = buildBars(archimedeStory);
        const gatewayEnd = gatewayBars.length ? Math.max(...gatewayBars.map((bar) => bar.endMs)) : null;
        const archimedeStart = archimedeBars.length ? Math.min(...archimedeBars.map((bar) => bar.startMs)) : null;
        const handoffGapMs = gatewayEnd != null && archimedeStart != null ? archimedeStart - gatewayEnd : 0;
        const handoffGap = handoffGapMs > 500
            ? {
                  duration: handoffGapMs,
                  left: ((gatewayEnd - traceStart) / totalWallMs) * 100,
                  width: Math.max(1.2, (handoffGapMs / totalWallMs) * 100),
              }
            : null;

        const combinedInput = sumBy(traceStories, (story) => story.input_tokens);
        const combinedOutput = sumBy(traceStories, (story) => story.output_tokens);
        return {
            requestId: activeStory.request_id,
            traceStart,
            totalWallMs,
            combinedTokens: sumBy(traceStories, (story) => story.total_tokens),
            combinedCost:
                (combinedInput / 1000000) * Number(costInputRate || 0) + (combinedOutput / 1000000) * Number(costOutputRate || 0),
            hasGateway: Boolean(gatewayStory),
            hasArchimede: Boolean(archimedeStory),
            gatewayBars,
            archimedeBars,
            handoffGap,
        };
    }, [activeStory, requestTraceStories, costInputRate, costOutputRate]);

    const featureLatencyChart = useMemo(() => {
        const bucketSize = datePreset === "today" ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
        const byBucket = new Map();

        filteredStories.forEach((story) => {
            const timestamp = toUnixMs(story.timestamp || story.started_at || story.ended_at);
            if (timestamp == null) return;
            const feature = story.__normalized_feature || "other";
            const bucketStart = Math.floor(timestamp / bucketSize) * bucketSize;
            const current = byBucket.get(bucketStart) || { bucketStart, features: new Map() };
            const featureCurrent = current.features.get(feature) || { count: 0, sumDuration: 0 };
            featureCurrent.count += 1;
            featureCurrent.sumDuration += Number(story.duration_ms || 0);
            current.features.set(feature, featureCurrent);
            byBucket.set(bucketStart, current);
        });

        const featuresWithData = FEATURE_LATENCY_ORDER.filter((feature) =>
            Array.from(byBucket.values()).some((bucket) => (bucket.features.get(feature)?.count || 0) > 0)
        );
        const rows = Array.from(byBucket.values())
            .sort((a, b) => a.bucketStart - b.bucketStart)
            .map((bucket) => {
                const row = {
                    bucket: dayjs(bucket.bucketStart).format(datePreset === "today" ? "MM-DD HH:00" : "MM-DD"),
                    bucketStart: bucket.bucketStart,
                };
                featuresWithData.forEach((feature) => {
                    const item = bucket.features.get(feature);
                    const count = Number(item?.count || 0);
                    row[feature] = count ? Math.round(Number(item?.sumDuration || 0) / count) : null;
                    row[`${feature}_count`] = count;
                });
                return row;
            });

        return { rows, featuresWithData };
    }, [filteredStories, datePreset]);

    const highlightedFeature = selectedFeaturesNormalized[0] || "";

    const anomalyFeed = useMemo(() => {
        return filteredStories
            .map((story) => ({
                ...story,
                anomalySignals: detectAnomalies(story),
            }))
            .filter((story) => story.anomalySignals.length > 0)
            .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime())
            .slice(0, 20);
    }, [filteredStories]);

    const controlRoom = useMemo(() => {
        const storiesWithSignals = filteredStories.map((story) => {
            const anomalySignals = detectAnomalies(story);
            const telemetry = getStoryTelemetry(story);
            const durationMs = Number(story.duration_ms || 0);
            const isFailure = story.status === "error";
            const isRecent = isRecentStory(story);
            const isTest = isTestStory(story);
            const hasRealClient = isRealActorId(story.client_id);
            const hasRealUser = isRealActorId(story.user_id);
            const hasRealImpact = hasRealClient || hasRealUser;
            const isCriticalFeature = isProductionCriticalFeature(story.__normalized_feature || story.feature || story.brain);
            const isVerySlow = durationMs > 60000 || telemetry.preStreamDurationMs > 10000 || telemetry.totalStreamDurationMs > 60000;
            const hasRetry = telemetry.retryCount > 0;
            const hasStreamMismatch = anomalySignals.some((signal) => signal.includes("stream chunk"));
            const severity = isFailure && isRecent && !isTest && hasRealImpact
                ? "critical"
                : !isTest && isRecent && (hasStreamMismatch || hasRetry || isVerySlow)
                  ? "urgent"
                  : anomalySignals.length
                    ? "watch"
                    : "ok";

            return {
                ...story,
                anomalySignals,
                operationalSeverity: severity,
                retryCount: telemetry.retryCount,
                durationMs,
                isRecentOperationalSignal: isRecent,
                isTestOperationalSignal: isTest,
                hasRealOperationalImpact: hasRealImpact,
                isCriticalProductionFeature: isCriticalFeature,
            };
        });

        const anomalousStories = storiesWithSignals.filter((story) => story.anomalySignals.length > 0 || story.status === "error");
        const testSignalStories = anomalousStories.filter((story) => story.isTestOperationalSignal);
        const operationalSignals = anomalousStories.filter((story) => !story.isTestOperationalSignal);
        const recentOperationalSignals = operationalSignals.filter((story) => story.isRecentOperationalSignal);
        const recentFailures = recentOperationalSignals.filter((story) => story.status === "error");
        const recentRealFailures = recentFailures.filter((story) => story.hasRealOperationalImpact);
        const recentSlowSignals = recentOperationalSignals.filter((story) => story.durationMs > 60000 || getStoryTelemetry(story).preStreamDurationMs > 10000);
        const recentRetrySignals = recentOperationalSignals.filter((story) => story.retryCount > 0);
        const productionStories = filteredStories.filter((story) => !isTestStory(story));
        const failedStories = productionStories.filter((story) => story.status === "error");
        const affectedClientValues = Array.from(new Set(recentOperationalSignals.map((story) => story.client_id).filter(isRealActorId)));
        const affectedUserValues = Array.from(new Set(recentOperationalSignals.map((story) => story.user_id).filter(isRealActorId)));
        const p95 = percentile(filteredStories.map((story) => story.duration_ms), 95);
        const recentP95 = percentile(
            filteredStories
                .filter((story) => isRecentStory(story) && !isTestStory(story))
                .map((story) => story.duration_ms),
            95
        );
        const failureRateValue = productionStories.length ? (failedStories.length / productionStories.length) * 100 : null;
        const avgTokens = filteredStories.length ? sumBy(filteredStories, (story) => story.total_tokens) / filteredStories.length : 0;
        const tokenSpikeFloor = Math.max(10000, avgTokens * 2);
        const costCandidates = filteredStories
            .filter((story) => isRecentStory(story) && !isTestStory(story) && Number(story.total_tokens || 0) >= tokenSpikeFloor)
            .sort((a, b) => Number(b.total_tokens || 0) - Number(a.total_tokens || 0))
            .slice(0, 5);

        const byFeature = new Map();
        filteredStories.filter((story) => !isTestStory(story)).forEach((story) => {
            const feature = story.__normalized_feature || "other";
            const current = byFeature.get(feature) || {
                feature,
                requests: 0,
                errors: 0,
                anomalies: 0,
                totalDuration: 0,
            };
            current.requests += 1;
            current.errors += story.status === "error" ? 1 : 0;
            current.anomalies += detectAnomalies(story).length ? 1 : 0;
            current.totalDuration += Number(story.duration_ms || 0);
            byFeature.set(feature, current);
        });

        const slowest = Array.from(byFeature.values())
            .map((item) => ({
                ...item,
                avgDurationMs: item.requests ? Math.round(item.totalDuration / item.requests) : 0,
                errorRate: item.requests ? (item.errors / item.requests) * 100 : 0,
            }))
            .sort((a, b) => b.avgDurationMs - a.avgDurationMs)[0] || null;

        const unstableFeature = Array.from(byFeature.values()).find((item) => item.errors >= 3 || (item.requests >= 3 && item.anomalies >= 3));
        const criticalCount = recentOperationalSignals.filter((story) => story.operationalSeverity === "critical").length;
        const urgentCount = recentOperationalSignals.filter((story) => story.operationalSeverity === "urgent").length;
        const repeatedRetries = recentOperationalSignals.filter((story) => story.retryCount > 0).length;
        const recentCriticalFeatureFailures = recentRealFailures.filter((story) => story.isCriticalProductionFeature);

        let severity = "OK";
        let severityTone = "ok";
        if (
            recentCriticalFeatureFailures.length > 0
            || (recentRealFailures.length > 0 && affectedClientValues.length > 1)
            || (recentRealFailures.length > 2)
        ) {
            severity = "Critique";
            severityTone = "critical";
        } else if (recentRealFailures.length > 0 || urgentCount >= 2 || repeatedRetries >= 2 || unstableFeature || (recentP95 != null && recentP95 > 60000)) {
            severity = "Urgent";
            severityTone = "urgent";
        } else if (recentOperationalSignals.length > 0 || (recentP95 != null && recentP95 > 15000) || costCandidates.length > 0) {
            severity = "A surveiller";
            severityTone = "watch";
        }

        const severityRank = { critical: 0, urgent: 1, watch: 2, ok: 3 };
        const urgentItems = operationalSignals
            .sort((a, b) => {
                const recentDelta = Number(b.isRecentOperationalSignal) - Number(a.isRecentOperationalSignal);
                if (recentDelta !== 0) return recentDelta;
                const impactDelta = Number(b.hasRealOperationalImpact) - Number(a.hasRealOperationalImpact);
                if (impactDelta !== 0) return impactDelta;
                const criticalFeatureDelta = Number(b.isCriticalProductionFeature) - Number(a.isCriticalProductionFeature);
                if (criticalFeatureDelta !== 0) return criticalFeatureDelta;
                const severityDelta = severityRank[a.operationalSeverity] - severityRank[b.operationalSeverity];
                if (severityDelta !== 0) return severityDelta;
                const tokenDelta = Number(b.total_tokens || 0) - Number(a.total_tokens || 0);
                if (Math.abs(tokenDelta) > 5000) return tokenDelta;
                return new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime();
            })
            .slice(0, 8);

        return {
            operationalSeverity: severity,
            severityTone,
            urgentStories: urgentItems,
            affectedClients: affectedClientValues,
            affectedUsers: affectedUserValues,
            slowestFeature: slowest,
            costSpikeCandidates: costCandidates,
            openAnomalyCount: recentOperationalSignals.length,
            periodSignalCount: anomalousStories.length,
            recentSignalCount: recentOperationalSignals.length,
            ignoredTestSignalCount: testSignalStories.length,
            activeIncidentCount: recentRealFailures.length,
            recentSlowSignalCount: recentSlowSignals.length,
            recentRetrySignalCount: recentRetrySignals.length,
            failureRate: failureRateValue,
            p95Latency: p95,
            recentP95Latency: recentP95,
            criticalCount,
            urgentCount,
            slowExecutionSignal: recentP95 != null && recentP95 > 15000,
        };
    }, [filteredStories]);

    const inspectorGroups = useMemo(() => {
        if (!groupByAnalysis) return [];
        const grouped = new Map();
        filteredStories.forEach((story) => {
            const key = story.analysis_id ? String(story.analysis_id) : "__no_analysis__";
            const current = grouped.get(key) || {
                analysisId: key,
                stories: [],
                totalTokens: 0,
                latestTs: 0,
            };
            current.stories.push(story);
            current.totalTokens += Number(story.total_tokens || 0);
            current.latestTs = Math.max(current.latestTs, toUnixMs(story.timestamp || story.started_at) || 0);
            grouped.set(key, current);
        });
        return Array.from(grouped.values()).sort((a, b) => b.latestTs - a.latestTs);
    }, [groupByAnalysis, filteredStories]);

    function jumpToInspector(story) {
        if (!story) return;
        setFilters({ activeView: "inspector" });
        if (story.request_id && !requestContextById[story.request_id]) {
            setIsRequestContextLoading(true);
            setRequestContextError("");
        }
        setSelectedRequestId(story.__selection_key || getStorySelectionKey(story));
        setChatMessages([]);
        setAiError("");
        setAutoDiagStatus("");
    }

    function buildAutoDiagnosisPrompt(story) {
        const telemetry = getStoryTelemetry(story);
        const stepsText = Array.isArray(story?.steps) && story.steps.length
            ? story.steps.map((step) => `${step.step || "step"}: ${Number(step.duration_ms || 0)}ms (${step.status || "unknown"})`).join("\n")
            : "none";
        const errorsText = Array.isArray(story?.errors) && story.errors.length ? JSON.stringify(story.errors, null, 2) : "none";
        const warningsText = Array.isArray(story?.warnings) && story.warnings.length ? JSON.stringify(story.warnings, null, 2) : "none";
        return `You are a debugging assistant for Archimede (AI legal analysis platform).
Task: produce a rigorous incident diagnosis in French, readable by a entrepreneur/operator.
Priority: be precise, debuggable, and honest about uncertainty.

Language constraints:
- Respond 100% in French.
- No English section titles or explanations.
- Keep log field names as-is only when necessary for traceability.

Critical reasoning rules:
- Separate clearly:
  1) observed facts from logs
  2) probable inferences
  3) open hypotheses (unconfirmed)
- Never present an inference as a fact.
- If evidence is missing, say "inconnu depuis les logs".
- Prefer formulations like:
  "les logs montrent...", "les logs ne montrent pas...", "cela suggere...", "hypothese ouverte...".
- Keep functional language first; technical detail only if useful for action.

Non-negotiable telemetry interpretation guardrails:
- Never conclude "empty stream" or "user received nothing" from chunks_emitted=0 unless stream_instrumentation_version >= 2 is explicit.
- If stream_instrumentation_version is missing/legacy, classify chunks_emitted and time_to_first_token_ms as "instrumentation inconclusive" unless corroborated by another signal.
- For service=verdictia-backend on proxy routes, llm_call_count=0 and models_used=[] are expected and must not be treated as anomalies.
- For TTFT interpretation:
  - prefer ttft_upstream_ms when present;
  - if only legacy time_to_first_token_ms exists and equals 0, treat as uncertain unless v2 telemetry confirms it.
- When uncertain, explicitly write: "inconnu depuis les logs" and avoid user-impact claims stated as facts.

REQUEST ID: ${story?.request_id || "none"}
STATUS: ${story?.status || "unknown"}
TOTAL DURATION: ${Number(story?.duration_ms || 0)}ms
SERVICE: ${story?.service || "unknown"}
FEATURE: ${story?.__normalized_feature || normalizeFeature(story?.feature)}
STEPS:
${stepsText}
TELEMETRY:
pre_stream_duration_ms: ${telemetry.preStreamDurationMs}
total_stream_duration_ms: ${telemetry.totalStreamDurationMs}
chunks_emitted: ${telemetry.chunksEmitted}
time_to_first_token_ms: ${telemetry.timeToFirstTokenMs}
ttft_control_ms: ${Number.isFinite(telemetry.ttftControlMs) ? telemetry.ttftControlMs : "n/a"}
ttft_upstream_ms: ${telemetry.ttftUpstreamMs ?? "n/a"}
stream_instrumentation_version: ${telemetry.streamInstrumentationVersion || "n/a"}
from_cache: ${telemetry.fromCache ? "true" : "false"}
has_final_event: ${telemetry.hasFinalEvent ? "true" : "false"}
stream_completed: ${telemetry.streamCompleted ? "true" : "false"}
cards_emitted: ${telemetry.cardsEmitted ?? "n/a"}
retry_count: ${telemetry.retryCount}
llm_call_count: ${telemetry.llmCallCount}
bedrock_reasoning_enabled: ${telemetry.bedrockReasoningEnabled ? "true" : "false"}
bedrock_reasoning_estimated_tokens: ${telemetry.bedrockReasoningEstimatedTokens}
bedrock_reasoning_estimated_cost_usd: ${telemetry.bedrockReasoningEstimatedCostUsd}
bedrock_reasoning_delta_count: ${telemetry.bedrockReasoningDeltaCount}
bedrock_reasoning_forwarded_count: ${telemetry.bedrockReasoningForwardedCount}
ERRORS: ${errorsText}
WARNINGS: ${warningsText}

Required output sections (in this exact order):
1) Ce qui s'est passe (vue fonctionnelle)
- 2-4 phrases max.
- Distinguish "fait observe" vs "inference probable".

2) Impact utilisateur probable
- Keep conditional wording (e.g. "si le frontend depend des chunks...").
- Do not state UX impact as certainty unless logs prove it.

3) Chronologie causale tracable
- 5-8 bullets max, ordered in time.
- Each bullet must include at least one evidence tag:
  [service:...] [step:...] [event:...] [duration:...] [timestamp:...]
- End this section with one explicit "incoherence finale" bullet if present.

4) Niveau de certitude
- Sous-sections:
  - Certain
  - Probable
  - Possible mais non confirme
- 2-5 bullets per subsection.

5) Cause probable (formulation rigoureuse)
- 1 short paragraph.
- Must identify the most likely failure zone.
- Must explicitly mention at least one alternative explanation.

6) Plan d'action concret
- 1 to 3 bullets max.
- Each action must be directly testable/verifiable in logs or metrics.

Formatting constraints:
- Plain text only.
- No markdown syntax (#, *, **, or triple-backticks).
- Use "-" for bullets only.`;
    }

    async function runAutoDiagnosisForStory(story) {
        if (!story) return;
        setAiChatOpen(true);
        setChatMessages([]);
        setAiError("");
        setAutoDiagStatus("Auto-diagnosis started...");
        const prompt = buildAutoDiagnosisPrompt(story);
        const result = await askAiExplanation(prompt, {
            messageRole: "auto-diagnosis",
            visibleText: "Auto-diagnosis request generated from execution telemetry (human-readable + traceable).",
            severity: bugStorySeverity,
        });
        if (result?.ok) {
            setAutoDiagStatus("Auto-diagnosis complete.");
        } else {
            setAutoDiagStatus("Auto-diagnosis failed. Check error message below.");
        }
    }

    async function runAutoDiagnosis() {
        await runAutoDiagnosisForStory(activeStory);
    }

    function inspectControlRoomStory(story) {
        if (!story) return;
        jumpToInspector(story);
    }

    function filterErrorsFromControlRoom() {
        setFilters({ activeView: "overview", status: "error", sortBy: "latest" });
    }

    function showSlowRequestsFromControlRoom() {
        setFilters({ activeView: "overview", minDurationMs: "15000", sortBy: "latency_desc" });
    }

    function buildIncidentBriefingText() {
        const topIssue = controlRoom.urgentStories[0];
        const lines = [
            `Etat global: ${controlRoom.operationalSeverity}`,
            `${controlRoom.activeIncidentCount} incidents actifs`,
            `${controlRoom.recentSignalCount} signaux recents`,
            `${controlRoom.periodSignalCount} signaux detectes sur la periode`,
            `${controlRoom.ignoredTestSignalCount} signaux de test ignores`,
            `Taux d'echec: ${controlRoom.failureRate == null ? "Non disponible" : `${controlRoom.failureRate.toFixed(1)}%`}`,
            `P95 recent: ${controlRoom.recentP95Latency == null ? "Non disponible" : formatDurationMs(controlRoom.recentP95Latency)}`,
            `Clients touches: ${controlRoom.affectedClients.length ? controlRoom.affectedClients.map(getClientLabel).join(", ") : "aucun detecte"}`,
            `Utilisateurs touches: ${controlRoom.affectedUsers.length ? controlRoom.affectedUsers.map(getUserLabel).join(", ") : "aucun detecte"}`,
            topIssue
                ? `Priorite: ${topIssue.__normalized_feature || "other"} - ${topIssue.anomalySignals.join(" | ")} - ${topIssue.request_id || topIssue.execution_id || "sans trace id"}`
                : "Priorite: aucune action urgente detectee",
        ];
        return lines.join("\n");
    }

    async function copyIncidentBriefing() {
        const text = buildIncidentBriefingText();
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            downloadText("archimede-control-room-briefing.txt", text, "text/plain");
        }
    }

    function renderFeatureLatencyTooltip({ active, payload, label }) {
        if (!active || !payload?.length) return null;
        return (
            <div className="acc-feature-latency-tooltip">
                <p className="acc-tip-label">{label}</p>
                {payload.map((entry) => {
                    const feature = entry?.dataKey;
                    const count = Number(entry?.payload?.[`${feature}_count`] || 0);
                    if (!Number.isFinite(entry.value)) return null;
                    return (
                        <p key={`tip-${feature}`} className="acc-tip-row">
                            <span className="acc-tip-dot" style={{ background: entry.fill || entry.color || "#0B3D91" }} />
                            <span className="acc-tip-feature">{feature}</span>
                            <span className="acc-tip-sep">&#183;</span>
                            <span>moy. {formatDurationMs(entry.value)}</span>
                            <span className="acc-tip-count">{count} req.</span>
                        </p>
                    );
                })}
            </div>
        );
    }

    return (
        <div className={`acc-root ${darkMode ? "acc-dark" : ""}`}>
            <div className="acc-page">
                <header className="acc-header">
                    <div>
                        <p className="acc-eyebrow">super-admin · observabilité</p>
                        <h1>Archimede Control Center</h1>
                        <p className="acc-subtitle">Comprendre l'usage, traquer les signaux, débugger en profondeur — verdictia-backend × archimede.</p>
                    </div>
                    <div className="acc-header-actions">
                        <button type="button" className="acc-ghost-btn" onClick={() => setDarkMode((prev) => !prev)}>
                            {darkMode ? <Sun size={14} /> : <Moon size={14} />}
                            <span>{darkMode ? "clair" : "sombre"}</span>
                        </button>
                        <button type="button" className="acc-ghost-btn" onClick={handleRefresh}>
                            <RefreshCw size={14} className={isRefreshing ? "acc-spin" : ""} />
                            <span>Rafraîchir</span>
                        </button>
                        <button type="button" className="acc-ghost-btn" onClick={exportVisibleAsJson}>
                            <Download size={14} />
                            <span>JSON</span>
                        </button>
                        <button type="button" className="acc-ghost-btn" onClick={exportVisibleAsCsv}>
                            <Download size={14} />
                            <span>CSV</span>
                        </button>
                    </div>
                </header>

                <section className="acc-vision-strip">
                    <article className="acc-vision-card">
                        <p className="acc-vision-label">Battement plateforme</p>
                        <p className="acc-vision-value">{formatNumber(kpis.totalRequests)}</p>
                        <p className="acc-vision-meta">{hasLoadedData ? `${uniqueActiveUsers} utilisateurs actifs sur la période` : "N.A"}</p>
                    </article>
                    <article className="acc-vision-card">
                        <p className="acc-vision-label">Fiabilité</p>
                        <p className="acc-vision-value">{displayNAWhenEmpty(`${kpis.successRate.toFixed(1)}%`, hasLoadedData)}</p>
                        <p className="acc-vision-meta">{displayNAWhenEmpty(`${formatDurationMs(avgLatencyMs)} latence moyenne`, hasLoadedData)}</p>
                    </article>
                    <article className="acc-vision-card">
                        <div className="acc-vision-headline">
                            <p className="acc-vision-label">Focus exécution</p>
                            <div className="acc-focus-controls">
                                <button
                                    type="button"
                                    className="acc-focus-arrow"
                                    onClick={() =>
                                        setFocusFeatureIndex((prev) =>
                                            sortedFeatures.length ? (prev - 1 + sortedFeatures.length) % sortedFeatures.length : 0
                                        )
                                    }
                                    disabled={!sortedFeatures.length}
                                >
                                    <ChevronLeft size={14} />
                                </button>
                                <button
                                    type="button"
                                    className="acc-focus-arrow"
                                    onClick={() =>
                                        setFocusFeatureIndex((prev) =>
                                            sortedFeatures.length ? (prev + 1) % sortedFeatures.length : 0
                                        )
                                    }
                                    disabled={!sortedFeatures.length}
                                >
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>
                        <p className="acc-vision-value">{displayNAWhenEmpty(focusedFeature?.name || topFeature, hasLoadedData)}</p>
                        {hasLoadedData && focusedFeature ? (
                            <div className="acc-vision-meta-grid">
                                <span className="acc-vision-meta-chip">{formatNumber(focusedFeature.value)} requêtes</span>
                                <span className="acc-vision-meta-chip">
                                    {formatUsd(focusedFeatureCost)} coût estimé · {formatNumber(focusedFeatureTokenUsage.total)} tokens
                                </span>
                                <span className="acc-vision-meta-chip acc-vision-meta-chip--thinking">
                                    thinking {formatNumber(focusedThinkingUsage.tokens)} tokens ({formatUsd(focusedThinkingUsage.cost)}) focus /{" "}
                                    {formatNumber(totalThinkingUsage.tokens)} total
                                </span>
                                {focusedThinkingUsage.tokens <= 0 && focusedThinkingUsage.deltas > 0 ? (
                                    <span className="acc-vision-meta-chip">
                                        {formatNumber(focusedThinkingUsage.deltas)} deltas thinking non facturables
                                    </span>
                                ) : null}
                                <span className="acc-vision-meta-chip">{formatNumber(thinkingSignalsCount)} traces de raisonnement</span>
                            </div>
                        ) : (
                            <p className="acc-vision-meta">$0.0000 coût estimé</p>
                        )}
                    </article>
                    <button type="button" className="acc-data-studio-toggle" onClick={() => setShowDataStudio((prev) => !prev)}>
                        {showDataStudio ? "Fermer le data studio" : "Ouvrir le data studio"}
                    </button>
                </section>

                {showDataStudio ? (
                    <section className="acc-ingestion-panel">
                        <div className="acc-ingestion-head">
                            <h2>Data studio</h2>
                            <label className="acc-upload-btn" htmlFor="acc-upload-input">
                                <Upload size={14} />
                                <span>Upload</span>
                            </label>
                            <input id="acc-upload-input" type="file" accept=".txt,.log,.json" onChange={onFileUpload} hidden />
                        </div>
                        <textarea
                            className="acc-log-textarea"
                            value={rawText}
                            onChange={(event) => setRawText(event.target.value)}
                            placeholder="paste mixed INFO + JSON logs"
                        />
                        <div className="acc-parse-meta">
                            <span>{parsedData.parseErrors.length} parse issues</span>
                            <span>{parsedData.infoLogs.length} INFO lines</span>
                            <span>{parsedData.executionStories.length} execution stories</span>
                            {dataLoadError ? <span>{dataLoadError}</span> : null}
                        </div>
                    </section>
                ) : null}

                <section className="acc-filter-bar">
                    <div className="acc-view-tabs desktop-only">
                        {VIEW_OPTIONS.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                className={`acc-view-tab ${activeView === tab.key ? "active" : ""}`}
                                onClick={() => setFilters({ activeView: tab.key })}
                            >
                                {tab.icon}
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>
                    <div className="acc-filter-controls">
                        <Select value={datePreset} options={RANGE_OPTIONS} className="acc-compact-select" onChange={(value) => setFilters({ datePreset: value })} />
                        {datePreset === "custom" ? (
                            <DatePicker.RangePicker
                                size="middle"
                                value={customDateStart && customDateEnd ? [dayjs(customDateStart), dayjs(customDateEnd)] : null}
                                onChange={(range) =>
                                    setFilters({
                                        customDateStart: range?.[0]?.toISOString() || null,
                                        customDateEnd: range?.[1]?.toISOString() || null,
                                    })
                                }
                            />
                        ) : null}
                        <Select
                            value={status}
                            className="acc-compact-select"
                            options={[
                                { value: "all", label: "all" },
                                { value: "success", label: "success" },
                                { value: "error", label: "error" },
                            ]}
                            onChange={(value) => setFilters({ status: value })}
                        />
                        <Select
                            value={selectedService}
                            className="acc-compact-select"
                            options={[{ value: "all", label: "service: all" }, ...allServices.map((service) => ({ value: service, label: service }))]}
                            onChange={(value) => setFilters({ selectedService: value })}
                        />
                        <Select
                            value={selectedModel}
                            className="acc-compact-select"
                            options={[{ value: "all", label: "model: all" }, ...allModels.map((model) => ({ value: model, label: model }))]}
                            onChange={(value) => setFilters({ selectedModel: value })}
                        />
                        <Select
                            value={selectedClient || "all"}
                            className="acc-compact-select"
                            options={[{ value: "all", label: "client: all" }, ...allClients.map((client) => ({ value: client, label: getClientLabel(client) }))]}
                            onChange={(value) => setFilters({ selectedClient: value === "all" ? "" : value })}
                        />
                        <Select
                            value={selectedUser || "all"}
                            className="acc-compact-select"
                            options={[{ value: "all", label: "user: all" }, ...allUsers.map((user) => ({ value: user, label: getUserLabel(user) }))]}
                            onChange={(value) => setFilters({ selectedUser: value === "all" ? "" : value })}
                        />
                        <Input
                            className="acc-mini-number"
                            type="number"
                            placeholder="min ms"
                            value={minDurationMs}
                            onChange={(event) => setFilters({ minDurationMs: event.target.value })}
                        />
                        <Input
                            className="acc-mini-number"
                            type="number"
                            placeholder="max ms"
                            value={maxDurationMs}
                            onChange={(event) => setFilters({ maxDurationMs: event.target.value })}
                        />
                        <Input
                            className="acc-mini-number"
                            type="number"
                            placeholder="min tokens"
                            value={minTokens}
                            onChange={(event) => setFilters({ minTokens: event.target.value })}
                        />
                        <Select
                            value={sortBy}
                            className="acc-compact-select"
                            options={[
                                { value: "latest", label: "sort: latest" },
                                { value: "latency_desc", label: "slowest first" },
                                { value: "latency_asc", label: "fastest first" },
                                { value: "tokens_desc", label: "most tokens" },
                            ]}
                            onChange={(value) => setFilters({ sortBy: value })}
                        />
                        <Input
                            allowClear
                            prefix={<Search size={14} />}
                            placeholder="Rechercher requête / user / client"
                            value={search}
                            onChange={(event) => setFilters({ search: event.target.value })}
                            className="acc-search-input"
                        />
                        <button
                            type="button"
                            className="acc-ghost-btn"
                            onClick={() => resetFilters()}
                        >
                            Réinitialiser
                        </button>
                    </div>
                    {(() => {
                        const chips = [];
                        if (status !== "all") chips.push({ key: "status", label: `statut: ${status}`, clear: () => setFilters({ status: "all" }) });
                        if (selectedService !== "all") chips.push({ key: "service", label: `service: ${selectedService}`, clear: () => setFilters({ selectedService: "all" }) });
                        if (selectedModel !== "all") chips.push({ key: "model", label: `modèle: ${selectedModel}`, clear: () => setFilters({ selectedModel: "all" }) });
                        if (selectedClient) chips.push({ key: "client", label: `client: ${getClientLabel(selectedClient)}`, clear: () => setFilters({ selectedClient: "" }) });
                        if (selectedUser) chips.push({ key: "user", label: `user: ${getUserLabel(selectedUser)}`, clear: () => setFilters({ selectedUser: "" }) });
                        if (minDurationMs) chips.push({ key: "min_duration", label: `≥ ${minDurationMs} ms`, clear: () => setFilters({ minDurationMs: "" }) });
                        if (maxDurationMs) chips.push({ key: "max_duration", label: `≤ ${maxDurationMs} ms`, clear: () => setFilters({ maxDurationMs: "" }) });
                        if (minTokens) chips.push({ key: "min_tokens", label: `tokens ≥ ${minTokens}`, clear: () => setFilters({ minTokens: "" }) });
                        if (search) chips.push({ key: "search", label: `« ${search} »`, clear: () => setFilters({ search: "" }) });
                        selectedFeaturesNormalized.forEach((feature) => chips.push({
                            key: `feature-${feature}`,
                            label: `feature: ${feature}`,
                            clear: () => setFilters({ selectedFeatures: selectedFeaturesNormalized.filter((item) => item !== feature) }),
                        }));
                        if (!chips.length) return null;
                        return (
                            <div className="acc-active-filters">
                                <span className="acc-active-filters-label">filtres actifs</span>
                                {chips.map((chip) => (
                                    <span key={chip.key} className="acc-active-chip">
                                        {chip.label}
                                        <button type="button" onClick={chip.clear} aria-label="retirer">×</button>
                                    </span>
                                ))}
                            </div>
                        );
                    })()}
                </section>

                {activeView === "control-room" ? (
                    <section className="acc-view-panel acc-control-room">
                        <section className={`acc-control-hero acc-control-hero--${controlRoom.severityTone}`}>
                            <div>
                                <p className="acc-control-kicker">Morning ops briefing</p>
                                <h2>Etat maintenant : {controlRoom.operationalSeverity}</h2>
                                <p className="acc-control-summary">
                                    {controlRoom.activeIncidentCount} incident critique actif ·{" "}
                                    {controlRoom.recentSlowSignalCount} lenteurs recentes ·{" "}
                                    {controlRoom.ignoredTestSignalCount} signaux de test ignores
                                </p>
                            </div>
                            <div className="acc-control-hero-metrics">
                                <span>{controlRoom.recentSignalCount} signaux recents</span>
                                <span>{controlRoom.recentP95Latency == null ? "Non disponible" : formatDurationMs(controlRoom.recentP95Latency)} P95 recent</span>
                                <span>{formatNumber(controlRoom.affectedClients.length)} clients reels touches</span>
                            </div>
                        </section>

                        <section className="acc-control-history-strip">
                            <span>Historique periode</span>
                            <strong>{formatNumber(controlRoom.periodSignalCount)} signaux detectes</strong>
                            <strong>{controlRoom.failureRate == null ? "Non disponible" : `${(100 - controlRoom.failureRate).toFixed(1)}%`} fiabilite</strong>
                            <strong>{formatDurationMs(avgLatencyMs)} latence moyenne</strong>
                        </section>

                        <div className="acc-control-card-grid">
                            <article className={`acc-control-card ${controlRoom.criticalCount || controlRoom.urgentCount ? "is-hot" : ""}`}>
                                <p>Stabilite IA</p>
                                <strong>{controlRoom.criticalCount ? "Anormal" : controlRoom.urgentCount ? "A traiter" : "Normal"}</strong>
                                <span>
                                    {controlRoom.recentSignalCount
                                        ? `${controlRoom.recentSignalCount} signaux recents hors tests`
                                        : "Aucun signal recent significatif"}
                                </span>
                            </article>
                            <article className={`acc-control-card ${controlRoom.slowExecutionSignal ? "is-warm" : ""}`}>
                                <p>Latence</p>
                                <strong>{controlRoom.slowExecutionSignal ? "Degradee" : "Normale"}</strong>
                                <span>
                                    {controlRoom.recentP95Latency == null
                                        ? "Non disponible"
                                        : `${formatDurationMs(controlRoom.recentP95Latency)} au P95 recent`}
                                </span>
                            </article>
                            <article className={`acc-control-card ${controlRoom.costSpikeCandidates.length ? "is-warm" : ""}`}>
                                <p>Couts / tokens</p>
                                <strong>{controlRoom.costSpikeCandidates.length ? "A surveiller" : "Normal"}</strong>
                                <span>
                                    {controlRoom.costSpikeCandidates.length
                                        ? `${controlRoom.costSpikeCandidates.length} execution(s) tres consommatrices`
                                        : `${formatNumber(kpis.totalTokens)} tokens sur la periode`}
                                </span>
                            </article>
                            <article className={`acc-control-card ${controlRoom.affectedUsers.length || controlRoom.affectedClients.length ? "is-warm" : ""}`}>
                                <p>Impact utilisateurs</p>
                                <strong>{controlRoom.affectedUsers.length ? `${formatNumber(controlRoom.affectedUsers.length)} users` : "Aucun impact detecte"}</strong>
                                <span>
                                    {controlRoom.affectedClients.length
                                        ? `${formatNumber(controlRoom.affectedClients.length)} client(s) reels concernes`
                                        : "Aucun client reel touche recemment"}
                                </span>
                            </article>
                        </div>

                        <div className="acc-control-main">
                            <article className="acc-card acc-control-watchlist">
                                <div className="acc-card-header">
                                    <h3>A regarder maintenant</h3>
                                    <Badge color={controlRoom.severityTone === "critical" ? STATUS_COLORS.error : controlRoom.severityTone === "urgent" ? STATUS_COLORS.warning : STATUS_COLORS.info}>
                                        {controlRoom.recentSignalCount} recents
                                    </Badge>
                                </div>
                                {controlRoom.urgentStories.length ? (
                                    <div className="acc-control-issue-list">
                                        {controlRoom.urgentStories.slice(0, 8).map((story) => {
                                            const impactParts = [
                                                story.client_id ? getClientLabel(story.client_id) : "",
                                                story.user_id ? getUserLabel(story.user_id) : "",
                                            ].filter(Boolean);
                                            return (
                                                <div key={`control-${story.__selection_key || story.__trace_key}-${story.timestamp}`} className={`acc-control-issue acc-control-issue--${story.operationalSeverity}`}>
                                                    <div className="acc-control-issue-top">
                                                        <span className="acc-control-severity">{story.operationalSeverity}</span>
                                                        <Badge color={FEATURE_COLORS[story.__normalized_feature] || FEATURE_COLORS.other}>{story.__normalized_feature || "other"}</Badge>
                                                        <span>{dayjs(story.timestamp || story.started_at).format("MM-DD HH:mm")}</span>
                                                    </div>
                                                    <p>{story.anomalySignals.join(" · ") || "Execution en erreur"}</p>
                                                    <div className="acc-control-issue-bottom">
                                                        <span>{impactParts.length ? impactParts.join(" · ") : "impact non disponible"}</span>
                                                        <button type="button" className="acc-anomaly-jump" onClick={() => inspectControlRoomStory(story)}>
                                                            Inspecter
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="acc-control-clear">
                                        <span className="acc-anomaly-clear-dot" />
                                        Rien ne brule sur la periode selectionnee. Premier controle: stable.
                                    </p>
                                )}
                            </article>

                            <aside className="acc-card acc-control-actions">
                                <div className="acc-card-header">
                                    <h3>Actions rapides</h3>
                                </div>
                                <button type="button" className="acc-control-action-btn" disabled={!controlRoom.urgentStories.length && !activeStory} onClick={() => inspectControlRoomStory(controlRoom.urgentStories[0] || activeStory)}>
                                    Inspecter la priorite
                                </button>
                                <button type="button" className="acc-control-action-btn" onClick={filterErrorsFromControlRoom}>
                                    Filtrer erreurs
                                </button>
                                <button type="button" className="acc-control-action-btn" onClick={showSlowRequestsFromControlRoom}>
                                    Voir requetes lentes
                                </button>
                                <button
                                    type="button"
                                    className="acc-control-action-btn"
                                    disabled={isAiLoading || (!controlRoom.urgentStories.length && !activeStory)}
                                    onClick={() => {
                                        const story = controlRoom.urgentStories[0] || activeStory;
                                        if (!story) return;
                                        inspectControlRoomStory(story);
                                        runAutoDiagnosisForStory(story);
                                    }}
                                >
                                    {isAiLoading ? "Diagnostic..." : "Diagnostic IA"}
                                </button>
                                <button type="button" className="acc-control-action-btn" onClick={copyIncidentBriefing}>
                                    <Copy size={14} />
                                    Copier briefing incident
                                </button>
                            </aside>
                        </div>
                    </section>
                ) : null}

                {activeView === "security" ? (
                    <section className="acc-view-panel acc-security-view">
                        <section className={`acc-security-hero ${securityStats.locked || securityStats.suspiciousIpRows.length ? "is-hot" : securityStats.failed ? "is-warm" : ""}`}>
                            <div>
                                <p className="acc-control-kicker">Auth security</p>
                                <h2>{securityStats.locked || securityStats.suspiciousIpRows.length ? "A surveiller" : "Connexion stable"}</h2>
                                <p className="acc-control-summary">
                                    {formatNumber(securityStats.failed)} échecs · {formatNumber(securityStats.locked)} blocages ·{" "}
                                    {formatNumber(securityStats.success)} succès sur la période
                                </p>
                            </div>
                            <div className="acc-control-hero-metrics">
                                <span>{formatNumber(securityStats.total)} events auth</span>
                                <span>{(securityStats.failureRate * 100).toFixed(1)}% échec/blocage</span>
                                <span>{formatNumber(securityStats.suspiciousIpRows.length)} IP à regarder</span>
                            </div>
                        </section>

                        {securityLoadError ? <p className="acc-error-text">{securityLoadError}</p> : null}

                        <div className="acc-security-card-grid">
                            <article className={`acc-control-card ${securityStats.failed ? "is-warm" : ""}`}>
                                <p>Login failed</p>
                                <strong>{formatNumber(securityStats.failed)}</strong>
                                <span>{formatNumber(securityStats.unknownEmail)} email inconnue</span>
                            </article>
                            <article className={`acc-control-card ${securityStats.locked ? "is-hot" : ""}`}>
                                <p>Account locked</p>
                                <strong>{formatNumber(securityStats.locked)}</strong>
                                <span>rate-limit et blocages temporaires</span>
                            </article>
                            <article className={`acc-control-card ${securityStats.suspiciousIpRows.length ? "is-hot" : ""}`}>
                                <p>IP weird</p>
                                <strong>{formatNumber(securityStats.suspiciousIpRows.length)}</strong>
                                <span>échecs répétés ou emails testés</span>
                            </article>
                            <article className="acc-control-card">
                                <p>Login success</p>
                                <strong>{formatNumber(securityStats.success)}</strong>
                                <span>connexions réussies corrélables</span>
                            </article>
                        </div>

                        <div className="acc-security-main">
                            <article className="acc-card">
                                <div className="acc-card-header">
                                    <h3>Signaux récents</h3>
                                    <Badge color={securityStats.locked ? STATUS_COLORS.error : securityStats.failed ? STATUS_COLORS.warning : STATUS_COLORS.info}>
                                        {formatNumber(filteredSecurityEvents.length)} events
                                    </Badge>
                                </div>
                                {filteredSecurityEvents.length ? (
                                    <div className="acc-table-wrap">
                                        <table className="acc-table acc-security-table">
                                            <thead>
                                                <tr>
                                                    <th>Heure</th>
                                                    <th>Event</th>
                                                    <th>Raison</th>
                                                    <th>IP</th>
                                                    <th>Email</th>
                                                    <th>Request</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredSecurityEvents.slice(0, 80).map((event) => {
                                                    const tone = securityEventTone(event.event, event.severity);
                                                    return (
                                                        <tr key={event.__key}>
                                                            <td>{event.timestamp_display}</td>
                                                            <td>
                                                                <span className="acc-inline-status">
                                                                    <StatusDot status={tone} />
                                                                    <Badge color={SECURITY_EVENT_COLORS[event.event] || STATUS_COLORS.info}>{event.event}</Badge>
                                                                </span>
                                                            </td>
                                                            <td>{event.reason || "N.A"}</td>
                                                            <td>{event.ip || "unknown"}</td>
                                                            <td>
                                                                <span className="acc-security-hash">{event.email_domain || "N.A"}</span>
                                                                <small>{shortenId(event.email_hash)}</small>
                                                            </td>
                                                            <td>{shortenId(event.request_id)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="acc-empty-wrap">
                                        <Empty description="Aucun event auth sur la période" />
                                    </div>
                                )}
                            </article>

                            <aside className="acc-card acc-security-ip-panel">
                                <div className="acc-card-header">
                                    <h3>IP à regarder</h3>
                                </div>
                                {securityStats.suspiciousIpRows.length ? (
                                    <div className="acc-security-ip-list">
                                        {securityStats.suspiciousIpRows.slice(0, 10).map((row) => (
                                            <div key={row.ip} className="acc-security-ip-row">
                                                <div>
                                                    <strong>{row.ip}</strong>
                                                    <span>{row.domains.slice(0, 3).join(", ") || "domaines inconnus"}</span>
                                                </div>
                                                <div>
                                                    <Badge color={row.locked ? STATUS_COLORS.error : STATUS_COLORS.warning}>
                                                        {row.failed} fails · {row.locked} locks
                                                    </Badge>
                                                    <small>{row.unknownEmail} unknown email</small>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="acc-control-clear">
                                        <span className="acc-anomaly-clear-dot" />
                                        Aucun pattern IP suspect détecté.
                                    </p>
                                )}
                            </aside>
                        </div>
                    </section>
                ) : null}

                {activeView === "overview" ? (
                    <section className="acc-view-panel">
                        <div className="acc-section-title">Briefing du matin
                            <span className="acc-section-title-count">{`plage : ${datePreset}`}</span>
                        </div>
                        <div className="acc-kpi-grid">
                            <KpiCard 
                                label={
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        Requêtes totales
                                        <InfoTooltip 
                                            title="Requêtes totales"
                                            description="Nombre total de requêtes LLM (Archimede + Gateway) capturées dans la période sélectionnée. Inclut toutes les features : contract_profile_inventory, contract_profile_selection, summary, metadata_guess, etc."
                                        />
                                    </span>
                                }
                                value={formatNumber(kpis.totalRequests)} 
                                secondary={`plage : ${datePreset}`} 
                            />
                            <KpiCard
                                label={
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        Taux de succès
                                        <InfoTooltip 
                                            title="Taux de succès"
                                            description="Pourcentage de requêtes qui se sont terminées avec succès (status: success). Un taux <95% indique des problèmes à investiguer dans la section Anomalies."
                                        />
                                    </span>
                                }
                                value={displayNAWhenEmpty(`${kpis.successRate.toFixed(1)}%`, hasLoadedData)}
                                secondary={hasLoadedData ? `${formatNumber(kpis.totalRequests - Math.round((kpis.successRate / 100) * kpis.totalRequests))} erreurs` : "N.A"}
                            />
                            <KpiCard
                                label={
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        Efficacité tokens
                                        <InfoTooltip 
                                            title="Efficacité tokens"
                                            description="Ratio output / total tokens pour Archimede. Une bonne efficacité (>40%) signifie qu'Archimede génère beaucoup de texte utile vs. consommer de l'input. Un ratio faible peut indiquer des prompts trop longs."
                                        />
                                    </span>
                                }
                                value={displayNAWhenEmpty(`${kpis.tokenEfficiencyPct.toFixed(1)}%`, hasLoadedData)}
                                secondary={
                                    hasLoadedData ? (
                                        <span className="acc-token-efficiency-wrap">
                                            <span className="acc-token-efficiency-bar" aria-hidden="true">
                                                <span className="acc-token-efficiency-fill" style={{ width: `${Math.max(0, Math.min(100, kpis.tokenEfficiencyPct))}%` }} />
                                            </span>
                                            <span>{formatNumber(kpis.archimedeOutputTokens)} out / {formatNumber(kpis.archimedeTotalTokens)} total</span>
                                        </span>
                                    ) : (
                                        "N.A"
                                    )
                                }
                            />
                            <KpiCard
                                label={
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        Coût estimé
                                        <InfoTooltip 
                                            title="Coût estimé"
                                            description="Coût total estimé en USD basé sur les tarifs input/output configurés. Inclut les tokens visibles + les tokens de raisonnement Bedrock (extended thinking)."
                                        />
                                    </span>
                                }
                                value={formatUsd(kpis.estimatedCost)}
                                secondary={`inclut ${formatUsd(kpis.bedrockReasoningEstimatedCost)} de raisonnement Bedrock`}
                            />
                            <KpiCard
                                label={
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        Raisonnement Bedrock
                                        <InfoTooltip 
                                            title="Raisonnement Bedrock (Extended Thinking)"
                                            description="Tokens de 'thinking' générés par les modèles Bedrock avec extended thinking activé. Ces tokens ne sont pas visibles par l'utilisateur mais sont facturés. Ils permettent au modèle de réfléchir avant de répondre."
                                        />
                                    </span>
                                }
                                value={formatNumber(kpis.bedrockReasoningEstimatedTokens)}
                                secondary={`${formatUsd(kpis.bedrockReasoningEstimatedCost)} estimé · tokens output`}
                            />
                        </div>

                        <div className="acc-rate-config">
                            <label>
                                input rate / 1M
                                <input type="number" step="0.01" value={costInputRate} onChange={(e) => setCostInputRate(Number(e.target.value || 0))} />
                            </label>
                            <label>
                                output rate / 1M
                                <input type="number" step="0.01" value={costOutputRate} onChange={(e) => setCostOutputRate(Number(e.target.value || 0))} />
                            </label>
                        </div>

                        <div className="acc-section-title">Signaux à traiter
                            <span className="acc-section-title-count">{anomalyFeed.length ? `${anomalyFeed.length} anomalies` : "tout est OK"}</span>
                        </div>
                        <article className="acc-card">
                            <div className="acc-card-header">
                                <h3>Anomalies & signaux</h3>
                            </div>
                            {anomalyFeed.length ? (
                                <div className="acc-anomaly-feed">
                                    {anomalyFeed.map((story) => (
                                        <div key={`anomaly-${story.__trace_key}-${story.timestamp}`} className="acc-anomaly-row">
                                            <span>{dayjs(story.timestamp).format("MM-DD HH:mm:ss")}</span>
                                            <Badge color={FEATURE_COLORS[story.__normalized_feature] || FEATURE_COLORS.other}>{story.__normalized_feature}</Badge>
                                            <span className="acc-inline-status">
                                                <StatusDot status={story.status} />
                                                {story.status}
                                            </span>
                                            <span className="acc-anomaly-signal">{story.anomalySignals.join(" · ")}</span>
                                            <button type="button" className="acc-anomaly-jump" onClick={() => jumpToInspector(story)}>
                                                {story.request_id ? shortenId(story.request_id) : "no trace id"}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="acc-anomaly-clear">
                                    <span className="acc-anomaly-clear-dot" />
                                    Tout est clair — aucune anomalie sur la période sélectionnée
                                </p>
                            )}
                        </article>

                        <div className="acc-section-title">Patterns d'usage
                            <span className="acc-section-title-count">distribution des features et latences</span>
                        </div>
                        <div className="acc-grid-two">
                            <article className="acc-card">
                                <div className="acc-card-header">
                                    <h3>Volume de requêtes</h3>
                                </div>
                                {isRefreshing ? (
                                    <Skeleton />
                                ) : (
                                    <ResponsiveContainer width="100%" height={220}>
                                        <BarChart data={volumeData}>
                                            <CartesianGrid strokeDasharray="4 4" stroke="rgba(127,127,127,0.2)" />
                                            <XAxis dataKey="key" tick={{ fontSize: 12 }} />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip contentStyle={{ border: "none", borderRadius: 10, background: "#1a1a18", color: "#fff" }} />
                                            <Bar dataKey="archimede" fill={SERVICE_COLORS.archimede} radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="verdictia-backend" fill={SERVICE_COLORS["verdictia-backend"]} radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </article>

                            <article className="acc-card">
                                <div className="acc-card-header">
                                    <h3>Usage par feature</h3>
                                </div>
                                {isRefreshing ? (
                                    <Skeleton />
                                ) : featureData.length ? (
                                    <ResponsiveContainer width="100%" height={220}>
                                        <PieChart>
                                            <Pie data={featureData} dataKey="value" nameKey="name" outerRadius={80} innerRadius={45}>
                                                {featureData.map((entry) => (
                                                    <Cell key={entry.name} fill={FEATURE_COLORS[entry.name] || FEATURE_COLORS.other} />
                                                ))}
                                            </Pie>
                                            <Tooltip contentStyle={{ border: "none", borderRadius: 10, background: "#1a1a18", color: "#fff" }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Empty description="No features" />
                                )}
                                <div className="acc-pill-row">
                                    {(selectedFeaturesNormalized.length ? selectedFeaturesNormalized : allFeatures).map((feature) => (
                                        <button
                                            type="button"
                                            key={feature}
                                            className={`acc-feature-pill ${selectedFeaturesNormalized.includes(feature) ? "active" : ""}`}
                                            onClick={() => toggleFeatureFilter(feature)}
                                        >
                                            <Badge color={FEATURE_COLORS[feature] || FEATURE_COLORS.other}>{feature}</Badge>
                                        </button>
                                    ))}
                                </div>
                            </article>

                            <article className="acc-card">
                                <div className="acc-card-header">
                                    <h3>Distribution des modèles</h3>
                                </div>
                                {isRefreshing ? (
                                    <Skeleton />
                                ) : (
                                    <ResponsiveContainer width="100%" height={220}>
                                        <AreaChart data={modelUsage}>
                                            <CartesianGrid strokeDasharray="4 4" stroke="rgba(127,127,127,0.2)" />
                                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip contentStyle={{ border: "none", borderRadius: 10, background: "#1a1a18", color: "#fff" }} />
                                            <Area type="monotone" dataKey="value" stroke="#0B3D91" fill="#0B3D9122" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                )}
                            </article>

                            <article className="acc-card">
                                <div className="acc-card-header">
                                    <h3>Histogramme des latences</h3>
                                </div>
                                {isRefreshing ? (
                                    <Skeleton />
                                ) : (
                                    <ResponsiveContainer width="100%" height={220}>
                                        <BarChart data={latencyHistogram}>
                                            <CartesianGrid strokeDasharray="4 4" stroke="rgba(127,127,127,0.2)" />
                                            <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip contentStyle={{ border: "none", borderRadius: 10, background: "#1a1a18", color: "#fff" }} />
                                            <Bar dataKey="count" fill="#1E5BB8" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </article>

                            <article className="acc-card acc-span-2">
                                <div className="acc-card-header">
                                    <h3>Latence par feature dans le temps</h3>
                                </div>
                                {isRefreshing ? (
                                    <Skeleton />
                                ) : featureLatencyChart.rows.length && featureLatencyChart.featuresWithData.length ? (
                                    <ResponsiveContainer width="100%" height={240}>
                                        <BarChart data={featureLatencyChart.rows}>
                                            <CartesianGrid strokeDasharray="4 4" stroke="rgba(127,127,127,0.2)" />
                                            <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip content={renderFeatureLatencyTooltip} />
                                            {featureLatencyChart.featuresWithData.map((feature) => (
                                                <Bar
                                                    key={`latency-${feature}`}
                                                    dataKey={feature}
                                                    fill={FEATURE_COLORS[feature] || FEATURE_COLORS.other}
                                                    radius={[4, 4, 0, 0]}
                                                    opacity={highlightedFeature && highlightedFeature !== feature ? 0.25 : 0.92}
                                                />
                                            ))}
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Empty description="No feature latency data for current filter" />
                                )}
                            </article>
                        </div>

                        <div className="acc-section-title">Flux temps réel
                            <span className="acc-section-title-count">20 dernières exécutions</span>
                        </div>
                        <article className="acc-card">
                            <div className="acc-card-header">
                                <h3>Live tail</h3>
                            </div>
                            {isRefreshing ? (
                                <Skeleton height={180} />
                            ) : liveTail.length ? (
                                <div className="acc-table-wrap">
                                    <table className="acc-table">
                                        <thead>
                                            <tr>
                                                <th>quand</th>
                                                <th>feature</th>
                                                <th>statut</th>
                                                <th>durée</th>
                                                <th>user</th>
                                                <th>tokens</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {liveTail.map((story) => (
                                                <tr key={`${story.__trace_key}-${story.timestamp}`} className="acc-clickable" onClick={() => jumpToInspector(story)}>
                                                    <td>{dayjs(story.timestamp).format("YYYY-MM-DD HH:mm:ss")}</td>
                                                    <td>
                                                        <Badge color={FEATURE_COLORS[story.__normalized_feature] || FEATURE_COLORS.other}>{story.__normalized_feature}</Badge>
                                                    </td>
                                                    <td>
                                                        <span className="acc-inline-status">
                                                            <StatusDot status={story.status} />
                                                            {story.status}
                                                        </span>
                                                    </td>
                                                    <td>{formatDurationMs(story.duration_ms)}</td>
                                                    <td>{getUserLabel(story.user_id)}</td>
                                                    <td>{formatNumber(story.total_tokens)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <Empty description="Aucune donnée pour ce filtre" />
                            )}
                        </article>
                    </section>
                ) : null}

                {activeView === "usage" ? (
                    <section className="acc-view-panel">
                        <div className="acc-section-title">Cartographie clients
                            <span className="acc-section-title-count">drill-down progressif vers les utilisateurs</span>
                        </div>
                        <div className="acc-usage-shell">
                            <aside className="acc-usage-clients">
                                <div className="acc-card-header">
                                    <h3>Clients</h3>
                                </div>
                                {clientAggregates.length ? (
                                    <div className="acc-client-list">
                                        {clientAggregates.map((client) => {
                                            const active = activeClientAggregate?.client_id === client.client_id;
                                            return (
                                                <button
                                                    key={client.client_id}
                                                    type="button"
                                                    className={`acc-client-card ${active ? "active" : ""}`}
                                                    onClick={() => {
                                                        setActiveClientId(client.client_id);
                                                        setFilters({ selectedClient: "", selectedUser: "" });
                                                    }}
                                                >
                                                    <div className="acc-client-card-head">
                                                        <p>{getClientLabel(client.client_id)}</p>
                                                        <Badge color="#0B3D91">{formatUsd(client.cost)}</Badge>
                                                    </div>
                                                    <div className="acc-client-card-meta">
                                                        <span>{shortenId(client.client_id)}</span>
                                                        <span>{formatNumber(client.requests)} requests</span>
                                                        <span>{formatNumber(client.unique_users)} users</span>
                                                        <span>{formatNumber(client.tokens)} tokens</span>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="acc-empty-wrap">
                                        <Empty description="No clients for current filters" />
                                    </div>
                                )}
                            </aside>

                            <article className="acc-usage-users">
                                <div className="acc-card-header">
                                    <h3>Utilisateurs · {getClientLabel(activeClientAggregate?.client_id || "")}</h3>
                                </div>
                                {activeClientAggregate ? (
                                    <div className="acc-usage-summary">
                                        <Badge color="#1E5BB8">{formatNumber(activeClientAggregate.requests)} requêtes</Badge>
                                        <Badge color="#2B9B95">{formatNumber(activeClientAggregate.tokens)} tokens</Badge>
                                        <Badge color="#0B3D91">{formatUsd(activeClientAggregate.cost)}</Badge>
                                        <Badge color="#8C8C89">{formatNumber(activeClientAggregate.unique_users)} utilisateurs</Badge>
                                    </div>
                                ) : null}
                                {isRefreshing ? (
                                    <Skeleton height={220} />
                                ) : activeClientUsers.length ? (
                                    <div className="acc-table-wrap">
                                        <table className="acc-table">
                                            <thead>
                                                <tr>
                                                    <th>user_id</th>
                                                    <th>requests</th>
                                                    <th>tokens</th>
                                                    <th>avg latency</th>
                                                    <th>features</th>
                                                    <th>last active</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {activeClientUsers.map((user) => (
                                                    <tr
                                                        key={user.user_id}
                                                        className="acc-clickable"
                                                        onClick={() => {
                                                            setHistoryTarget(user);
                                                            setHistoryPage(1);
                                                            setHistoryDrawerOpen(true);
                                                        }}
                                                    >
                                                        <td>
                                                            <div>{getUserLabel(user.user_id)}</div>
                                                            <small>{shortenId(user.user_id)}</small>
                                                        </td>
                                                        <td>{formatNumber(user.requests)}</td>
                                                        <td>{formatNumber(user.tokens)}</td>
                                                        <td>{formatDurationMs(user.avg_latency_ms)}</td>
                                                        <td>
                                                            <div className="acc-inline-badges">
                                                                {user.features.map((feature) => (
                                                                    <Badge key={`${user.user_id}-${feature}`} color={FEATURE_COLORS[feature] || FEATURE_COLORS.other}>
                                                                        {feature}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td>{user.last_active ? dayjs(user.last_active).format("YYYY-MM-DD HH:mm") : "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="acc-empty-wrap">
                                        <Empty description="No users for selected client and filters" />
                                    </div>
                                )}
                            </article>
                        </div>
                    </section>
                ) : null}

                {activeView === "inspector" ? (
                    <section className="acc-inspector-view">
                        <aside className="acc-request-list">
                            <div className="acc-card-header">
                                <h3>Requêtes</h3>
                                <button type="button" className={`acc-group-toggle ${groupByAnalysis ? "active" : ""}`} onClick={() => setGroupByAnalysis((prev) => !prev)}>
                                    Grouper par analyse
                                </button>
                            </div>
                            <div className="acc-request-list-body">
                                {groupByAnalysis
                                    ? inspectorGroups.map((group) => {
                                          const groupLabel = group.analysisId === "__no_analysis__" ? "no analysis id" : shortenId(group.analysisId);
                                          const expanded = analysisGroupExpanded[group.analysisId] ?? true;
                                          return (
                                              <div key={`analysis-${group.analysisId}`} className="acc-analysis-group">
                                                  <button
                                                      type="button"
                                                      className="acc-analysis-header"
                                                      onClick={() =>
                                                          setAnalysisGroupExpanded((prev) => ({
                                                              ...prev,
                                                              [group.analysisId]: !(prev[group.analysisId] ?? true),
                                                          }))
                                                      }
                                                  >
                                                      <span>{groupLabel}</span>
                                                      <span>{formatNumber(group.stories.length)} requests</span>
                                                      <span>{formatNumber(group.totalTokens)} tokens</span>
                                                  </button>
                                                  {expanded ? (
                                                      <div className="acc-analysis-body">
                                                          {group.stories.map((story) => {
                                                              const active = activeStory?.__selection_key === story.__selection_key;
                                                              return (
                                                                  <button
                                                                      key={`${story.__trace_key}-${story.execution_id}-${story.timestamp}`}
                                                                      type="button"
                                                                      className={`acc-request-row ${active ? "active" : ""}`}
                                                                      onClick={() => {
                                                                          if (story.request_id && !requestContextById[story.request_id]) {
                                                                              setIsRequestContextLoading(true);
                                                                              setRequestContextError("");
                                                                          }
                                                                          setSelectedRequestId(story.__selection_key || story.__trace_key);
                                                                          setChatMessages([]);
                                                                          setAiError("");
                                                                          setAutoDiagStatus("");
                                                                      }}
                                                                  >
                                                                      <div className="acc-request-head">
                                                                          <span>{story.request_id ? clipRequestId(story.request_id) : shortenId(story.execution_id)}</span>
                                                                          {!story.request_id ? <Badge color="#8C8C89">no trace id</Badge> : null}
                                                                          <Badge color={FEATURE_COLORS[story.__normalized_feature] || FEATURE_COLORS.other}>{story.__normalized_feature}</Badge>
                                                                      </div>
                                                                      <div className="acc-request-meta">
                                                                          <span className="acc-inline-status">
                                                                              <StatusDot status={story.status} />
                                                                              {story.status}
                                                                          </span>
                                                                          <span>{story.service || "unknown service"}</span>
                                                                          <span>{inferOperationType(story)}</span>
                                                                          <span>{formatDurationMs(story.duration_ms)}</span>
                                                                          <span>{dayjs(story.timestamp).format("MM-DD HH:mm")}</span>
                                                                          <span>{getUserLabel(story.user_id)}</span>
                                                                      </div>
                                                                  </button>
                                                              );
                                                          })}
                                                      </div>
                                                  ) : null}
                                              </div>
                                          );
                                      })
                                    : filteredStories.map((story) => {
                                          const active = activeStory?.__selection_key === story.__selection_key;
                                          return (
                                              <button
                                                  key={`${story.__trace_key}-${story.execution_id}-${story.timestamp}`}
                                                  type="button"
                                                  className={`acc-request-row ${active ? "active" : ""}`}
                                                  onClick={() => {
                                                      if (story.request_id && !requestContextById[story.request_id]) {
                                                          setIsRequestContextLoading(true);
                                                          setRequestContextError("");
                                                      }
                                                      setSelectedRequestId(story.__selection_key || story.__trace_key);
                                                      setChatMessages([]);
                                                      setAiError("");
                                                      setAutoDiagStatus("");
                                                  }}
                                              >
                                                  <div className="acc-request-head">
                                                      <span>{story.request_id ? clipRequestId(story.request_id) : shortenId(story.execution_id)}</span>
                                                      {!story.request_id ? <Badge color="#8C8C89">no trace id</Badge> : null}
                                                      <Badge color={FEATURE_COLORS[story.__normalized_feature] || FEATURE_COLORS.other}>{story.__normalized_feature}</Badge>
                                                  </div>
                                                  <div className="acc-request-meta">
                                                      <span className="acc-inline-status">
                                                          <StatusDot status={story.status} />
                                                          {story.status}
                                                      </span>
                                                      <span>{story.service || "unknown service"}</span>
                                                      <span>{inferOperationType(story)}</span>
                                                      <span>{formatDurationMs(story.duration_ms)}</span>
                                                      <span>{dayjs(story.timestamp).format("MM-DD HH:mm")}</span>
                                                      <span>{getUserLabel(story.user_id)}</span>
                                                  </div>
                                              </button>
                                          );
                                      })}
                            </div>
                        </aside>

                        <div className="acc-request-detail">
                            {activeStory ? (
                                <>
                                    {hasBugStorySignal ? (
                                        <article className={`acc-bug-story-banner ${bugStorySeverity}`}>
                                            <p>Quelque chose semble anormal sur cette requête.</p>
                                            <button type="button" className="acc-primary-btn acc-bug-story-btn" disabled={isAiLoading} onClick={runAutoDiagnosis}>
                                                {isAiLoading ? "Analyse..." : "Auto-diagnostic"}
                                            </button>
                                            {autoDiagStatus ? <small className="acc-bug-story-status">{autoDiagStatus}</small> : null}
                                        </article>
                                    ) : null}
                                    <header className="acc-story-header">
                                        <div>
                                            <h3>
                                                request {activeStory.request_id ? clipRequestId(activeStory.request_id) : shortenId(activeStory.execution_id)}
                                                {!activeStory.request_id ? <Badge color="#8C8C89">no trace id</Badge> : null}
                                                <button type="button" className="acc-copy-btn" onClick={() => copyRequestId(activeStory.request_id || activeStory.execution_id)}>
                                                    <Copy size={12} />
                                                </button>
                                            </h3>
                                            <p>{activeStory.execution_id}</p>
                                        </div>
                                        <div className="acc-inline-badges">
                                            <Badge color={FEATURE_COLORS[activeStory.__normalized_feature] || FEATURE_COLORS.other}>{activeStory.__normalized_feature}</Badge>
                                            <Badge color={STATUS_COLORS[activeStory.status] || STATUS_COLORS.info}>{activeStory.status}</Badge>
                                            <Badge color="#1E5BB8">{activeStory.model || "unknown model"}</Badge>
                                            <Badge color={SERVICE_COLORS[activeStory.service] || "#8C8C89"}>{activeStory.service || "unknown service"}</Badge>
                                        </div>
                                    </header>
                                    {isInspectorContextBootstrapping ? (
                                        <div className="acc-request-loading-banner">
                                            <RefreshCw size={14} className="acc-spin" />
                                            <span>Loading full request trace...</span>
                                        </div>
                                    ) : null}

                                    <div className="acc-story-metrics">
                                        <span>{formatDurationMs(activeStory.duration_ms)}</span>
                                        <span>in {formatNumber(activeStory.input_tokens)} / out {formatNumber(activeStory.output_tokens)}</span>
                                        <span>{formatUsd(activeStory.cost_usd)}</span>
                                        {reasoningTelemetryVisible ? (
                                            <span>
                                                out split {formatNumber(outputSplit.visibleOut)} visible + {formatNumber(outputSplit.reasoningTokens)} thinking
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="acc-story-root">
                                        <span>
                                            root request: <strong>{activeStory.request_id ? clipRequestId(activeStory.request_id) : "no trace id"}</strong>
                                        </span>
                                        <span>
                                            operation: <strong>{inferOperationType(activeStory)}</strong>
                                        </span>
                                        <span>
                                            source: <strong>{extractRouteHint(activeStory)}</strong>
                                        </span>
                                    </div>

                                    <article className="acc-card acc-streaming-breakdown-card">
                                        <div className="acc-card-header">
                                            <h3>Streaming breakdown</h3>
                                        </div>
                                        <div className="acc-streaming-waterfall">
                                            <div
                                                className="acc-streaming-segment pre-stream"
                                                style={{ width: `${streamBreakdown.prePct}%` }}
                                                title={`pre-stream: ${formatDurationMs(streamBreakdown.preStream)}`}
                                            />
                                            <div
                                                className="acc-streaming-segment streaming"
                                                style={{ width: `${streamBreakdown.streamPct}%` }}
                                                title={`streaming: ${formatDurationMs(streamBreakdown.streamOnly)}`}
                                            />
                                        </div>
                                        <div className="acc-streaming-meta">
                                            <span>TTFT: {telemetryValueOrNA(activeStoryTelemetry.timeToFirstTokenMs)}</span>
                                            <span>Total stream: {telemetryValueOrNA(activeStoryTelemetry.totalStreamDurationMs)}</span>
                                            <span>Total request: {formatDurationMs(streamBreakdown.totalDuration)}</span>
                                        </div>
                                        {reasoningTelemetryVisible ? (
                                            <div className="acc-output-richness">
                                                <span className="acc-output-chip">
                                                    Bedrock reasoning: {formatNumber(activeStoryTelemetry.bedrockReasoningEstimatedTokens)} tokens
                                                </span>
                                                <span className="acc-output-chip">
                                                    {formatUsd(activeStoryTelemetry.bedrockReasoningEstimatedCostUsd)} est.
                                                </span>
                                                <span className="acc-output-chip">
                                                    cost split {formatUsd(outputSplit.visibleCost)} visible + {formatUsd(outputSplit.reasoningCost)} thinking
                                                </span>
                                                <span className="acc-output-chip">
                                                    {formatNumber(activeStoryTelemetry.bedrockReasoningDeltaCount)} deltas
                                                </span>
                                                <span className="acc-output-chip">
                                                    {formatNumber(activeStoryTelemetry.bedrockReasoningCharsReceived)} chars in / {formatNumber(activeStoryTelemetry.bedrockReasoningCharsForwarded)} chars out
                                                </span>
                                                <span className="acc-output-chip">
                                                    {formatNumber(activeStoryTelemetry.bedrockReasoningForwardedCount)} public messages
                                                </span>
                                                <span className="acc-output-chip">
                                                    budget {formatNumber(activeStoryTelemetry.bedrockReasoningBudgetTokens)}
                                                </span>
                                            </div>
                                        ) : null}
                                        {streamingAnomalyBadges.length ? (
                                            <div className="acc-streaming-anomalies">
                                                {streamingAnomalyBadges.map((flag) => (
                                                    <Badge key={flag.text} color={flag.kind === "error" ? "#E24B4A" : "#EF9F27"}>
                                                        {flag.text}
                                                    </Badge>
                                                ))}
                                            </div>
                                        ) : null}
                                        {outputRichness.length ? (
                                            <div className="acc-output-richness">
                                                {outputRichness.map((chip) => (
                                                    <span key={chip.label} className="acc-output-chip">
                                                        {chip.label}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : null}
                                    </article>

                                    <article className="acc-card">
                                        <div className="acc-card-header">
                                            <h3>Story telling</h3>
                                        </div>
                                        {isInspectorContextBootstrapping ? (
                                            <div className="acc-loading-skeleton-stack">
                                                <Skeleton height={20} />
                                                <Skeleton height={16} />
                                                <Skeleton height={16} />
                                                <Skeleton height={140} />
                                            </div>
                                        ) : null}
                                        {!isInspectorContextBootstrapping ? (
                                            <>
                                                {requestNarrative ? (
                                                    <div className="acc-storytelling">
                                                        <p>
                                                            <strong>{requestNarrative.title}.</strong> {requestNarrative.mainNeed}
                                                        </p>
                                                        <p>{requestNarrative.flow}</p>
                                                        <p>
                                                            Steps: {requestNarrative.stepsCount} | Event logs: {requestNarrative.eventsCount} | Finalize events: {requestNarrative.finalizeEventsCount}
                                                        </p>
                                                    </div>
                                                ) : null}
                                                <div className="acc-chronology-list">
                                                    {requestTimelineFeed.map((line, index) => (
                                                        <div key={`${line.kind}-${line.timestamp_ms}-${index}`} className="acc-chronology-row">
                                                            <span className="acc-inline-status">
                                                                <StatusDot status={line.status} />
                                                                {dayjs(line.timestamp_ms).format("YYYY-MM-DD HH:mm:ss")}
                                                            </span>
                                                            <span>{line.label}</span>
                                                            <span>{line.detail}</span>
                                                            <span>{line.offset_ms != null ? `+${formatDurationMs(line.offset_ms)}` : "N.A"}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        ) : null}
                                        {isInspectorContextBootstrapping ? <p className="acc-chat-empty">Loading full request context...</p> : null}
                                        {requestContextError ? <p className="acc-error-text">{requestContextError}</p> : null}
                                    </article>

                                    {dualServiceTrace ? (
                                        <article className="acc-card">
                                            <div className="acc-card-header">
                                                <h3>Unified trace</h3>
                                            </div>
                                            <div className="acc-trace-header">
                                                <span>request_id: <strong>{dualServiceTrace.requestId}</strong></span>
                                                <span>wall time: <strong>{formatDurationMs(dualServiceTrace.totalWallMs)}</strong></span>
                                                <span>tokens: <strong>{formatNumber(dualServiceTrace.combinedTokens)}</strong></span>
                                                <span>cost: <strong>{formatUsd(dualServiceTrace.combinedCost)}</strong></span>
                                                {!dualServiceTrace.hasArchimede ? <Badge color="#EF9F27">missing archimede trace</Badge> : null}
                                                {!dualServiceTrace.hasGateway ? <Badge color="#EF9F27">missing gateway trace</Badge> : null}
                                            </div>
                                            <div className="acc-dual-trace">
                                                <div className="acc-dual-trace-row">
                                                    <div className="acc-dual-trace-label">gateway (verdictia-backend)</div>
                                                    <div className="acc-dual-trace-track">
                                                        {dualServiceTrace.gatewayBars.map((bar) => (
                                                            <div
                                                                key={bar.key}
                                                                className="acc-dual-trace-bar"
                                                                style={{ left: `${bar.left}%`, width: `${bar.width}%`, backgroundColor: bar.color }}
                                                                title={`${bar.label} | ${formatDurationMs(bar.duration)} | ${bar.status}`}
                                                            />
                                                        ))}
                                                        {dualServiceTrace.handoffGap ? (
                                                            <div
                                                                className="acc-dual-trace-gap"
                                                                style={{ left: `${dualServiceTrace.handoffGap.left}%`, width: `${dualServiceTrace.handoffGap.width}%` }}
                                                                title={`handoff gap ${formatDurationMs(dualServiceTrace.handoffGap.duration)}`}
                                                            >
                                                                handoff gap {formatDurationMs(dualServiceTrace.handoffGap.duration)}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                                <div className="acc-dual-trace-row">
                                                    <div className="acc-dual-trace-label">ai engine (archimede)</div>
                                                    <div className="acc-dual-trace-track">
                                                        {dualServiceTrace.archimedeBars.map((bar) => (
                                                            <div
                                                                key={bar.key}
                                                                className="acc-dual-trace-bar"
                                                                style={{ left: `${bar.left}%`, width: `${bar.width}%`, backgroundColor: bar.color }}
                                                                title={`${bar.label} | ${formatDurationMs(bar.duration)} | ${bar.status}`}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="acc-dual-trace-axis">
                                                <span>{formatDurationMs(0)}</span>
                                                <span>{formatDurationMs(dualServiceTrace.totalWallMs)}</span>
                                            </div>
                                        </article>
                                    ) : activeStory?.request_id ? (
                                        <article className="acc-card">
                                            <div className="acc-card-header">
                                                <h3>Unified trace</h3>
                                            </div>
                                            <p className="acc-chat-empty">No trace timing available yet for this request_id.</p>
                                        </article>
                                    ) : null}

                                    <article className="acc-card">
                                        <div className="acc-card-header">
                                            <h3>Review finalize events</h3>
                                            <Badge color={requestReviewFinalizeLogs.length ? "#1E5BB8" : "#8C8C89"}>
                                                {requestReviewFinalizeLogs.length}
                                            </Badge>
                                        </div>
                                        {requestReviewFinalizeLogs.length ? (
                                            <div className="acc-finalize-list">
                                                {requestReviewFinalizeLogs.map((event, index) => {
                                                    const isError = event.event === "stream_error_forced_done";
                                                    const isReplay = event.event === "duplicate_finalize_ignored_or_replaced";
                                                    const color = isError ? "#E24B4A" : isReplay ? "#EF9F27" : "#1E5BB8";
                                                    return (
                                                        <div key={`${event.request_id}-${event.event}-${index}`} className="acc-finalize-row">
                                                            <span className="acc-inline-status">
                                                                <StatusDot status={isError ? "error" : "info"} />
                                                                {event.finalize_outcome || event.event || "review_finalize"}
                                                            </span>
                                                            <span>{event.finalize_reason || "unknown reason"}</span>
                                                            <span>{shortenId(event.document_version_id)}</span>
                                                            <span>{Array.isArray(event.criterion_ids) ? event.criterion_ids.length : 0} criteria</span>
                                                            <Badge color={color}>{event.__source_log_group || "backend"}</Badge>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="acc-chat-empty">No review finalize event found for this request_id.</p>
                                        )}
                                    </article>

                                    <article className="acc-card">
                                        <div className="acc-card-header">
                                            <h3>Timeline</h3>
                                        </div>
                                        <div className="acc-timeline">
                                            {(activeStory.steps || []).map((step) => {
                                                const offset = Math.max(0, new Date(step.started_at).getTime() - new Date(activeStory.started_at).getTime());
                                                const totalDuration = Number(activeStory.duration_ms || 1);
                                                const leftPct = (offset / totalDuration) * 100;
                                                const widthPct = (Number(step.duration_ms || 1) / totalDuration) * 100;
                                                return (
                                                    <div key={`${step.step}-${step.started_at}`} className="acc-timeline-row">
                                                        <div className="acc-timeline-step-name">{step.step}</div>
                                                        <div className="acc-timeline-track">
                                                            <div
                                                                className="acc-timeline-bar"
                                                                style={{
                                                                    left: `${leftPct}%`,
                                                                    width: `${Math.max(widthPct, 1.5)}%`,
                                                                    backgroundColor: STEP_COLORS[step.step] || "#0B3D91",
                                                                }}
                                                                title={`${step.step} | ${formatDurationMs(step.duration_ms)} | ${step.status}`}
                                                            />
                                                        </div>
                                                        <div className="acc-timeline-meta">{formatDurationMs(step.duration_ms)}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </article>

                                    <article className="acc-card">
                                        <div className="acc-card-header">
                                            <h3>Step details</h3>
                                        </div>
                                        <div className="acc-step-grid">
                                            {(activeStory.steps || []).map((step) => (
                                                <div className="acc-step-card" key={`${step.step}-${step.started_at}-card`}>
                                                    <p>{step.step}</p>
                                                    <span className="acc-inline-status">
                                                        <StatusDot status={step.status} />
                                                        {step.status}
                                                    </span>
                                                    <small>{formatDurationMs(step.duration_ms)}</small>
                                                </div>
                                            ))}
                                        </div>
                                    </article>

                                    {Array.isArray(activeStory.warnings) && activeStory.warnings.length ? (
                                        <article className="acc-card">
                                            <div className="acc-card-header">
                                                <h3>Warnings</h3>
                                            </div>
                                            {activeStory.warnings.map((warning, index) => (
                                                <div key={`warning-${index}`} className="acc-alert warning">
                                                    {warning.message || JSON.stringify(warning)}
                                                </div>
                                            ))}
                                        </article>
                                    ) : null}

                                    {Array.isArray(activeStory.errors) && activeStory.errors.length ? (
                                        <article className="acc-card">
                                            <div className="acc-card-header">
                                                <h3>Errors</h3>
                                            </div>
                                            {activeStory.errors.map((error, index) => (
                                                <div key={`error-${index}`} className="acc-alert error">
                                                    {error.message || JSON.stringify(error)}
                                                </div>
                                            ))}
                                        </article>
                                    ) : null}

                                    <article className="acc-card">
                                        <button type="button" className="acc-accordion-toggle" onClick={() => setShowRawLog((prev) => !prev)}>
                                            {showRawLog ? "Hide raw log" : "Show raw log"}
                                        </button>
                                        {showRawLog ? (
                                            <pre className="acc-raw-json">
                                                {JSON.stringify(
                                                    {
                                                        execution_story: activeStory,
                                                        request_execution_stories: requestExecutionStories,
                                                        event_logs: requestEventLogsMerged,
                                                        review_finalize_logs: requestReviewFinalizeLogs,
                                                        raw_cloudwatch_events: requestContext?.rawEvents || [],
                                                        timeline_feed: requestTimelineFeed,
                                                    },
                                                    null,
                                                    2
                                                )}
                                            </pre>
                                        ) : null}
                                    </article>

                                    <article className="acc-card acc-ai-panel">
                                        <div className="acc-card-header">
                                            <h3>Ask AI to explain</h3>
                                        </div>
                                        <p className="acc-chat-empty">Assistant is pinned at the bottom of the dashboard like a chatbot.</p>
                                        <button type="button" className="acc-primary-btn acc-ai-open-btn" onClick={() => setAiChatOpen(true)}>
                                            Open chatbot
                                        </button>
                                    </article>
                                </>
                            ) : (
                                <div className="acc-empty-wrap">
                                    <Empty description="Select a request to inspect" />
                                </div>
                            )}
                        </div>
                    </section>
                ) : null}
            </div>

            <section className={`acc-ai-chatbot ${aiChatOpen ? "open" : "collapsed"}`}>
                <header className="acc-ai-chatbot-header">
                    <div>
                        <strong>Assistant IA</strong>
                        <p>{activeStory ? `requête ${activeStory.request_id ? clipRequestId(activeStory.request_id) : shortenId(activeStory.execution_id)}` : "sélectionnez une requête pour discuter"}</p>
                    </div>
                    <button type="button" className="acc-ghost-btn" onClick={() => setAiChatOpen((prev) => !prev)}>
                        {aiChatOpen ? "Réduire" : "Ouvrir"}
                    </button>
                </header>

                {aiChatOpen ? (
                    <div className="acc-ai-chatbot-body">
                        <div className="acc-chat-thread acc-ai-chat-thread">
                            {chatMessages.length ? (
                                chatMessages.map((msg, index) => (
                                    <div key={`dock-msg-${index}`} className={`acc-chat-message ${msg.role} ${msg.severity ? `acc-${msg.severity}` : ""}`}>
                                        <p className="acc-chat-role">{msg.role}</p>
                                        <p className="acc-chat-body">{toHumanChatText(msg.text || "...")}</p>
                                    </div>
                                ))
                            ) : (
                                <p className="acc-chat-empty">No explanation yet.</p>
                            )}
                        </div>
                        {aiError ? <p className="acc-error-text">{aiError}</p> : null}
                        <div className="acc-ai-chatbot-input-row">
                            <Input
                                value={chatInput}
                                onChange={(event) => setChatInput(event.target.value)}
                                placeholder="Question de suivi"
                                disabled={!activeStory}
                                onPressEnter={() => {
                                    const text = chatInput.trim();
                                    if (!text || isAiLoading || !activeStory) return;
                                    setChatInput("");
                                    askAiExplanation(text);
                                }}
                            />
                            <button
                                type="button"
                                className="acc-primary-btn"
                                onClick={() => {
                                    const text = chatInput.trim();
                                    setChatInput("");
                                    askAiExplanation(text);
                                }}
                                disabled={isAiLoading || !activeStory}
                            >
                                {isAiLoading ? "..." : <Bot size={14} />}
                            </button>
                            <button
                                type="button"
                                className="acc-assistant-ask-btn"
                                disabled={isAiLoading || !activeStory}
                                onClick={() => askAiExplanation()}
                            >
                                {isAiLoading ? "Analyse..." : "Demander à l'IA"}
                            </button>
                        </div>
                    </div>
                ) : null}
            </section>

            <nav className="acc-mobile-tabbar">
                {VIEW_OPTIONS.map((tab) => (
                    <button key={`m-${tab.key}`} type="button" className={activeView === tab.key ? "active" : ""} onClick={() => setFilters({ activeView: tab.key })}>
                        {tab.icon}
                        <span>{tab.label.split(" ")[0]}</span>
                    </button>
                ))}
            </nav>

            <Drawer
                title={historyTarget ? `${getUserLabel(historyTarget.user_id)} request history` : "Request history"}
                placement="right"
                width={420}
                open={historyDrawerOpen}
                onClose={() => setHistoryDrawerOpen(false)}
            >
                <div className="acc-history-list">
                    {historyPaginated.map((story) => (
                        <article key={`${story.__trace_key || getStoryTraceKey(story)}-${story.timestamp}`} className="acc-history-card">
                            <div className="acc-inline-status">
                                <StatusDot status={story.status} />
                                <span>{story.status}</span>
                            </div>
                            <p>{story.request_id || `no trace id (${shortenId(story.execution_id)})`}</p>
                            <small>
                                {story.__normalized_feature || normalizeFeature(story.feature) || "other"} | {story.service || "unknown service"}
                            </small>
                            <small>{dayjs(story.timestamp).format("YYYY-MM-DD HH:mm:ss")}</small>
                            <small>
                                {formatDurationMs(story.duration_ms)} | {formatNumber(story.total_tokens)} tokens
                            </small>
                            <small>
                                TTFT {telemetryValueOrNA(getStoryTelemetry(story).timeToFirstTokenMs)} | pre{" "}
                                {telemetryValueOrNA(getStoryTelemetry(story).preStreamDurationMs)} | stream{" "}
                                {telemetryValueOrNA(getStoryTelemetry(story).totalStreamDurationMs)} | chunks{" "}
                                {getStoryTelemetry(story).chunksEmitted ?? "N.A"}
                            </small>
                        </article>
                    ))}
                </div>
                <div className="acc-history-pagination">
                    <button type="button" className="acc-ghost-btn" disabled={historyPage === 1} onClick={() => setHistoryPage((prev) => Math.max(1, prev - 1))}>
                        Prev
                    </button>
                    <span>{historyPage}</span>
                    <button
                        type="button"
                        className="acc-ghost-btn"
                        disabled={historyPage * historyPageSize >= userHistoryStories.length}
                        onClick={() => setHistoryPage((prev) => prev + 1)}
                    >
                        Next
                    </button>
                </div>
            </Drawer>
        </div>
    );
}

